<template>
  <!--
    `scrollable` only when the profile *is* the page. Embedded in a spotting area or a community it
    flows inside that page's pane instead — see isEmbeddedInParentPage for why nesting a second
    scroller there breaks both the sticky top bar and the wheel.
  -->
  <div
    class="foodleap-actor-detail"
    :class="isEmbeddedInParentPage ? 'has-parent-page' : 'scrollable'"
    ref="scrollable"
  >
    <template v-if="actor && actor.id">
      <div
        class="fixed right-5 top-[90px] z-[80] px-5 py-3 rounded-[4px] bg-[var(--fl-green)] text-white
               text-[13px] font-medium shadow-[0_12px_30px_rgba(51,65,62,0.2)]"
        role="status"
        v-if="toast"
      >{{ toast }}
      </div>

      <!-- ─────────── Top bar ─────────── -->
      <!--
        One right-aligned group: utilities first, then the calls to action at the trailing edge.
        The section nav used to hold the left of this bar and is a vertical rail now (fl-sidenav),
        so nothing competes with these for the bar's width — including the hero's own buttons, which
        were a second set of the same actions a scroll further down.
      -->
      <div class="fl-topbar">
        <div class="fl-topbar__utils">
          <!--
            The augmented profile is now what /actors/:id renders on Foodleap, so this is the only
            route to the old page. Owners only: it is a fallback for people who know the classic
            profile and want the fields it still shows, not something to offer a first-time visitor.
          -->
          <button class="fl-textbtn" v-if="showOwnerTools & isMember" @click="goToClassicProfile">Classic profile</button>
          <button class="fl-textbtn" @click="shareProfile('Top bar')">Share ↗</button>
          <!--
            Stays visible while previewing, because it is the only way back: the preview hides
            every other owner control, this one included if it were gated on showOwnerTools.
          -->
          <button
            class="fl-textbtn"
            v-if="canPreviewAsVisitor && isMember"
            :class="{ 'is-active': forcedVisitorPreview }"
            :aria-pressed="forcedVisitorPreview ? 'true' : 'false'"
            @click="toggleVisitorPreview"
          >{{ forcedVisitorPreview ? 'Exit visitor view' : 'View as visitor' }}
          </button>
          <button class="fl-textbtn" v-if="canRunOperations && isMember" @click="openOperations">Operations ⚙</button>
        </div>
        <div class="fl-topbar__actions">
          <button class="fl-btn fl-btn--dark fl-btn--small" v-if="showOwnerTools" @click="openEditor(0, 'Complete your profile')" v-tooltip.bottom="'Upgrade this profile to reflect the role ' +  actor.name + ' plays across the four\n'+
'            food transitions—and turn that intelligence into relevant partners, opportunities\n'+
'            and shared progress.'">
            Complete your profile <span aria-hidden="true">↗</span>
          </button>
          <!--
            Alongside the owner tools rather than instead of them. An ecosystem member can edit
            every actor, so gating the claim on `!showOwnerTools` hid it from exactly the people
            who came here to claim: the button never appeared for anyone who could also edit.
          -->
          <button
            class="fl-btn fl-btn--small"
            :class="showOwnerTools ? 'fl-btn--primary' : 'fl-btn--dark'"
            v-if="showClaimCta"
            @click="onClickClaimActor('Top bar')"
          >{{ $t('actor_claim') }} <span aria-hidden="true">↗</span></button>
          <!--
            Inviting the people who represent this actor, from the top of the page rather than only
            from the bottom of the Contacts section. Same modal as the "View contributors" control
            down there — it reads the actor from the route and reports back over
            `updateContributors`, which this page already listens for.
          -->
          <button
            class="fl-btn fl-btn--small fl-btn--ghost"
            v-if="canInviteContributors"
            @click="openContributorModal('Top bar')"
          >{{ $t('actor_view_contributors') }}
          </button>
          <!-- Straight to the people, from anywhere on the page. -->
          <button
            class="fl-btn fl-btn--small"
            :class="canUpgrade ? 'fl-btn--ghost' : 'fl-btn--dark'"
            v-if="showContactsSection"
            @click="scrollToSection('contacts', 'Top bar')"
          >{{ $t('actor_detail_contact') }} <span aria-hidden="true">↓</span></button>
          <!--
            On through the list the reader arrived from, the way the classic and simplified
            profiles do it. Only when there is a list to walk — see canGoToNext — otherwise the
            control would jump somewhere the reader never asked for.
          -->
          <button
            class="fl-btn fl-btn--ghost fl-btn--small"
            v-if="canGoToNext"
            @click="goToNext"
          >{{ $t('actor_detail_next') }} <span aria-hidden="true">→</span></button>
        </div>

      </div>

      <!--
        Profile navigation, as a rail down the left rather than a row in the top bar. Numbers only
        until you reach or hover a section, so it fits the page gutter instead of competing with the
        actions for the top bar's width.
      -->
      <nav class="fl-sidenav" aria-label="Profile sections">
        <button
          v-for="section of navSections"
          :key="section.key"
          :class="{ 'is-active': activeSection === section.key }"
          :aria-current="activeSection === section.key ? 'true' : null"
          @click="onNavClick(section)"
        >
          <b>{{ sectionNumbers[section.key] }}</b>
          <span>{{ section.label }}</span>
        </button>
      </nav>

      <!-- ─────────── Activation ─────────── -->
      <section class="fl-activation" ref="top">
        <div class="fl-loop fl-loop--a"></div>
        <div class="fl-loop fl-loop--b"></div>
        <div class="fl-activation__copy">
          <span class="fl-overline">Foodleap · Actor Profile</span>

          <!--
            Who this is, in the band that opens the page. It used to sit at the top of section 01,
            under a hero whose headline was the actor's name again — so the name appeared twice
            within a screen of itself and the identity was below the fold on a laptop.
          -->
          <div class="fl-profile-heading">
            <!-- `logo` may be a bare path, or absent with a logo.dev fallback — actorLogo resolves both. -->
            <div
              class="fl-logo"
              v-if="resolvedLogo"
              :style="{ backgroundImage: `url(${resolvedLogo})` }"
              :aria-label="actor.name"
            ></div>
            <div class="fl-logo" v-else>
              {{ initials }}
              <button v-if="showOwnerTools" aria-label="Add company logo" @click="openEditor(0, 'Add logo')">+</button>
            </div>
            <div>
              <span class="fl-verified" v-if="actor.claimed">Verified organisation ✓</span>
              <h1>{{ actor.name }}</h1>
              <p v-if="localizedActivitiesDescription">{{ localizedActivitiesDescription }}</p>
              <div class="fl-location" v-if="originLine">● {{ originLine }}</div>
              <!-- With the location, not between the blocks below it: both are identity metadata, so
                   the whole card header reads as one thing. One size, one shape — CardInfoSocial
                   sized each icon individually, which is why they never lined up. -->
              <div class="tw-flex flex-wrap items-center gap-2 mt-3" v-if="socialLinks.length">
                <a
                  class="tw-flex items-center justify-center w-8 h-8 rounded-full border
                         border-[var(--fl-line)] bg-white text-[var(--fl-ink)] no-underline
                         transition-colors hover:border-[var(--fl-green)] hover:text-[var(--fl-green)]"
                  v-for="link of visibleSocialLinks"
                  :key="link.name"
                  :href="link.href"
                  :target="link.external ? '_blank' : null"
                  :rel="link.external ? 'noopener' : null"
                  :title="link.title"
                  :aria-label="link.title"
                  @click="trackSocialLink(link)"
                >
                  <icon :name="link.icon" size="14"/>
                </a>

                <!--
                  The channels behind the login, shown as shape only. The icons are rendered without
                  their hrefs, so the blur is presentation rather than the protection: there is nothing
                  in the markup to read either.
                -->
                <button
                  class="fl-social-locked"
                  type="button"
                  v-if="lockedSocialLinks.length"
                  :aria-label="lockedSocialLabel"
                  @click="showSignIn('Locked channels')"
                >
                  <span class="fl-social-locked__icons" aria-hidden="true">
                    <span v-for="link of lockedSocialLinks" :key="link.name">
                      <icon :name="link.icon" size="14"/>
                    </span>
                  </span>
                  <span class="fl-social-locked__label">{{ lockedSocialLabel }}</span>
                </button>
              </div>
            </div>
            <!--            <button class="fl-editlink" v-if="showOwnerTools" @click="openEditor(0)">Edit organisation ↗</button>-->
          </div>
          <!-- The design's copy is written to the profile owner, so visitors get it in the third person. -->
          <!--          <p v-if="canUpgrade">
                      Upgrade this profile to reflect the role {{ actor.name }} plays across the four
                      food transitions—and turn that intelligence into relevant partners, opportunities
                      and shared progress.
                    </p>
                    <p v-else>
                      How this organisation contributes across the four food transitions—the roles it
                      plays, the technologies it works with, and where it is looking for partners.
                    </p>-->
        </div>

        <!-- The actor's own team sees why the upgrade is worth it; everyone else sees the snapshot. -->
        <aside class="fl-why-card" v-if="showUpgradeCard">
          <span class="fl-overline">Why upgrade?</span>
          <div class="fl-why-card__reason" v-for="reason of upgradeReasons" :key="reason.title">
            <b>{{ reason.number }}</b>
            <strong>{{ reason.title }}</strong>
            <p>{{ reason.body }}</p>
          </div>
          <!--
            Completeness lives here, not in the at-a-glance card it used to sit in. It is a measure
            of your own homework, so it belongs to the people who can do it — and this card is the
            one they see (showUpgradeCard is the claimed team, the glance card is everyone else).
          -->
          <div class="fl-health" v-if="profileCompleteness !== null">
            <span>Profile completeness</span><b>{{ profileCompleteness }}%</b>
            <i><em :style="{ width: profileCompleteness + '%' }"></em></i>
          </div>
          <small>About 8 minutes · You can save and continue</small>
        </aside>
        <!--
          A member who does not own this profile gets the actions rather than the snapshot. The
          numbers the snapshot carried — employees, locations — are already in section 01's glance
          row, so the hero's one card was spending its place restating them; what a reader cannot
          find anywhere else on the page is what they are able to do from here.
        -->
        <aside
          class="fl-why-card fl-why-card--cta"
          v-else-if="visitorActions.length"
          aria-label="Ways to reach this organisation"
        >
          <span class="fl-overline">Work with {{ actor.name }}</span>
          <button
            class="fl-why-card__action"
            type="button"
            v-for="action of visitorActions"
            :key="action.key"
            @click="runVisitorAction(action)"
          >
            <b>{{ action.number }}</b>
            <strong>{{ action.label }}</strong>
            <i aria-hidden="true">↗</i>
            <p>{{ action.note }}</p>
          </button>
          <!--          <small v-if="showContactsSection">Conversations stay on Foodleap — no contact details change hands.</small>-->
        </aside>
        <!--
          Anonymous visitors keep the snapshot. Every action above needs an account, and this page
          does not put members-only affordances in front of people who cannot use them.
        -->
        <aside class="fl-why-card fl-why-card--glance" v-else aria-label="Organisation snapshot">
          <span class="fl-overline">At a glance</span>
          <div class="fl-why-card__stat" v-for="stat of heroStats" :key="stat.label">
            <strong>{{ stat.value }}</strong><span>{{ stat.label }}</span>
          </div>
        </aside>

        <!--
          The hero's own bottom row, spanning both columns — inside the text column it would have to
          share the width with the card and the cells would wrap to one per line.
        -->
        <div class="fl-facts" v-if="factItems.length">
          <div v-for="fact of factItems" :key="fact.label" :class="{ 'is-primary': fact.primary }">
            <span>{{ fact.label }}</span>
            <strong>{{ fact.value }}</strong>
            <em v-if="fact.score !== undefined && fact.score !== null">{{ fact.score }}/100</em>
          </div>
        </div>
      </section>

      <!-- ─────────── 01 The organisation ─────────── -->
      <section class="fl-section fl-profile-intro" ref="story">
        <div class="fl-company-grid">
          <div class="fl-company-story">
            <span class="fl-section-no">{{ sectionNumbers.story }} / The organisation</span>
            <!--
              Every section an owner can edit carries the same control in the same place — top right
              of the section's header, under the overline once the page narrows — so the affordance
              is learnt once instead of hunted for per section. Each opens the wizard on the step
              that owns that section's fields.
            -->
            <button class="fl-section-edit" v-if="showOwnerTools" @click="openEditor(0, 'Edit organisation')">
              Edit organisation
            </button>
            <h3>{{ $t('actor_detail_about') }} {{ actor.name }}</h3>
            <RichBodyContent class="fl-copy" v-if="localizedDescription" :html="localizedDescription"/>
            <p class="fl-copy" v-else>{{ $t('actor_detail_insufficient_info') }}</p>
            <div class="fl-readmore" v-if="descriptionIsTruncatable">
              <button @click="toggleDescription">
                {{ showFullDescription ? 'Read less' : 'Read more' }}
              </button>
            </div>
            <div class="fl-glance" v-if="glanceItems.length">
              <div v-for="item of glanceItems" :key="item.label">
                <b>{{ item.value }}</b><span>{{ item.label }}</span>
              </div>
            </div>

            <!--
              Provenance for the actor's own team: when the profile last moved, and the log of what
              moved. Paired the way the classic profile's Completeness aside pairs them, rather than
              putting a bare date in one place and a link in another.
            -->
            <p class="fl-provenance" v-if="isActorTeam">
              Last updated on
              <Date :date="actor.updated_at" format="DD/MM/YYYY"/>
              <router-link
                :to="`/actors/${actor.id}/changes`"
                @click="trackCta('View all changes', 'Owner tools')"
              >View all changes ↗
              </router-link>
            </p>
          </div>

          <aside class="fl-products-card" v-if="productItems.length || showOwnerTools">
            <div class="fl-card-head">
              <span>Products &amp; services</span>
              <button v-if="showOwnerTools" @click="openEditor(7, 'Edit products')">Edit</button>
            </div>
            <a
              v-for="(product, index) of productItems"
              :key="'product' + product.id"
              @click="handleClickActorCard(product, 'Product')"
            >
              <b>{{ pad(index + 1) }}</b><span>{{ product.name }}</span><i>↗</i>
            </a>
            <small v-if="productItems.length">Links point to the product profiles on Foodleap.</small>
            <small v-else>No products or services on record yet.</small>
          </aside>
        </div>

        <!-- Expertise and the goals share one panel: both answer "what does this organisation
             stand for", and a second sand block for the icons alone read as a repeat of the
             first. -->
        <div class="fl-expertise" v-if="expertiseGroups.length || sustainabilityGoalItems.length">
          <div v-if="expertiseGroups.length">
            <span class="fl-section-no">{{ expertiseTitle }}</span>
            <h3>What they know. And where it matters.</h3>
            <p>
              Their expertise is built from confirmed activities, technologies and innovation domains and can be reviewed and updated by the organisation.
            </p>
            <!--
              In the copy column, under the sentence that promises it is editable — a corner of the
              panel would have put it over the domain rows, which belong to the other column, and
              this is a panel inside section 01 rather than a section with a header of its own.

              Only on the expertise branch: the SDG-only variant of this panel is about the UN goals,
              which this step does not edit.
            -->
            <button
              class="fl-section-edit fl-section-edit--inline"
              v-if="showOwnerTools"
              @click="openEditor(5, 'Edit expertise')"
            >
              Edit expertise
            </button>
          </div>
          <!-- Without expertise the panel would carry a heading about it and show only icons. -->
          <div v-else>
            <span class="fl-section-no">Impact</span>
            <h3>The goals this work serves.</h3>
            <p>
              The UN goals {{ actor.name }} contributes to. Select one to find every organisation
              on Foodleap working towards it.
            </p>
          </div>
          <div class="fl-expertise__domains" v-if="expertiseGroups.length">
            <!--
              Main domain left, the subdomains checked under it beside it on the same row. The list
              is capped rather than complete, so the count below is the control that lifts the cap:
              the panel's claim is that the basis is visible, and a count that only announced what
              was missing read as a control and did nothing when it was clicked.
            -->
            <div v-for="group of expertiseGroups" :key="group.key">
              <b>{{ group.label }}</b>
              <span class="fl-expertise__subs" v-if="group.children.length">
                <span v-for="child of group.children" :key="child.key">{{ child.label }}</span>
                <!-- Muted rather than in the accent: the toggle below is the row's only control,
                     and two green counts read as two of them. -->
                <span class="is-count" v-if="group.hidden">+{{ group.hidden }} more</span>
              </span>
            </div>
            <button
              class="is-more"
              type="button"
              v-if="hasHiddenExpertise"
              :aria-expanded="String(expertiseExpanded)"
              @click="toggleExpertise"
            >
              {{ expertiseToggleLabel }}
            </button>
          </div>

          <!-- Rendered here rather than with ActorDetailSDGs: that component's scoped styles are
               built for the legacy banner (right-aligned, fixed max-widths, clipped at 195px), and
               scoped styles cannot be reshaped from this page. Same images. -->
          <div class="fl-sdgs" v-if="sustainabilityGoalItems.length">
            <span class="fl-sdgs__label" v-if="expertiseGroups.length">
              Sustainable Development Goals
            </span>
            <div class="fl-sdgs__row">
              <!-- The tooltip names the goal *and* what the icon does: these read as badges, so a
                   tooltip carrying only the goal's name left the jump to the directory looking
                   like the page had navigated on its own. -->
              <button
                v-for="goal of sustainabilityGoalItems"
                :key="'sdg' + goal.number"
                v-tooltip.top="sustainabilityGoalTooltip(goal)"
                :aria-label="sustainabilityGoalTooltip(goal)"
                @click="filterBySustainabilityGoal(goal)"
              >
                <img :src="goal.image" :alt="goal.label" loading="lazy">
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- ─────────── 02 Transition relevance ─────────── -->
      <section class="fl-section fl-transitions" v-if="showTransitionsSection" ref="transitions">
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">{{ sectionNumbers.transitions }} / Transition relevance</span>
            <button class="fl-section-edit" v-if="showOwnerTools" @click="openEditor(3, 'Edit transitions')">
              Edit transitions
            </button>
            <h2>From ambition<br>to evidence</h2>
          </div>
          <!-- The second sentence is about the panel below, which a visitor does not get. -->
          <p>
            Each score combines readiness to scale, strategic impact and connectedness. The evidence and context behind the score are as important as the number itself.
          </p>
        </div>

        <!--
          Rendered from one entry up, not two. The tabs are the only place a score appears now, so
          hiding the row for a single-transition actor left the section with no number on it at all.
        -->
        <div
          class="fl-transition-tabs"
          :class="{ 'is-static': !isLoggedIn }"
          :role="isLoggedIn ? 'tablist' : null"
          aria-label="Food transitions"
          v-if="transitionEntries.length"
        >
          <!--
            For a visitor these are score blocks, not tabs: there is no detail panel below for them
            to switch between, so the tab semantics and the click are dropped rather than left to
            announce a control that does nothing.
          -->
          <button
            v-for="(entry, index) of transitionEntries"
            :key="entry.key"
            type="button"
            :role="isLoggedIn ? 'tab' : null"
            :disabled="!isLoggedIn"
            :aria-selected="isLoggedIn ? String(selectedTransitionIndex === index) : null"
            :style="{ '--fl-accent': entry.color }"
            @click="selectTransition(index, entry)"
          >
            <b>{{ pad(index + 1) }}</b>
            <span>{{ entry.label }}</span>
            <em v-if="entry.score !== null">{{ entry.score }}</em>
          </button>
        </div>

        <!--
          Visitors only: the ambition each actor states for the transition, one card per transition,
          under the score blocks. A member reads the same text as the headline of the detail panel
          below, which a visitor does not get — so rather than a caption squeezed into a score block,
          it gets the room the other cards on this page get. Public claim, no login needed.
        -->
        <div class="fl-transition-statements" v-if="!isLoggedIn && visitorStatements.length">
          <article
            v-for="entry of visitorStatements"
            :key="'statement' + entry.key"
            :style="{ '--fl-accent': entry.color }"
          >
            <span class="fl-overline">{{ entry.label }}</span>
            <p>{{ entry.contribution }}</p>
          </article>
        </div>

        <div
          class="fl-transition-detail"
          v-if="isLoggedIn && transition"
          :style="{ '--fl-accent': transition.color }"
        >
          <!--
            The ring and the three bars are one reading — the overall score and what it is made of —
            so they share a column, ring above bars, rather than sitting at opposite ends of the
            panel with the narrative wedged between them.
          -->
          <div class="fl-transition-scores" v-if="transition.score !== null || transition.metrics.length">
            <div class="fl-score-block" v-if="transition.score !== null">
              <div class="fl-score-ring" :style="{ '--fl-score': (transition.score * 3.6) + 'deg' }">
                <strong>{{ transition.score }}</strong><small>/100</small>
              </div>
              <span v-if="transition.role">{{ transition.role }}</span>
            </div>

            <div class="fl-metrics" v-if="transition.metrics.length">
              <div v-for="metric of transition.metrics" :key="metric.label">
                <span>{{ metric.label }}</span>
                <b>{{ metric.value }}</b>
                <i><em :style="{ width: metric.value + '%' }"></em></i>
              </div>
              <small v-if="transition.source">{{ transition.source }}</small>
            </div>
          </div>

          <div class="fl-transition-content">
            <span class="fl-overline">{{ transition.label }}</span>
            <h3>{{ transition.contribution || transition.role || 'Transition contributor' }}</h3>
            <div class="fl-domain-tags" v-if="domainNames.length">
              <span v-for="name of domainNames" :key="name">{{ name }}</span>
            </div>
            <div class="fl-narrative" v-if="narrativeCells.length">
              <div v-for="cell of narrativeCells" :key="cell.label">
                <span>{{ cell.label }}</span>
                <p>{{ cell.value }}</p>
              </div>
            </div>
          </div>
        </div>

        <!--
          What actually moves the score. Owners only: it is a set of instructions, and only someone
          who can edit the profile can act on them.

          The copy here is deliberately conservative. Several scoring criteria are switched off in
          the current model, and the impact axis is assessed from the actor's public website rather
          than from anything in this form — so promising that a field will move a number is easy to
          get wrong. See the block comment above the component for the field-by-field mapping.
          IMPROVE SCORE
        -->
        <!--        <details class="fl-score-help" v-if="showOwnerTools">
                  <summary>
                    <b>How to improve this score</b>
                    <span>What Foodleap can read, what information is still missing, and where it can be updated.</span>
                  </summary>

                  <p class="fl-score-help__lead">
                    <strong>Filling things in makes your score accurate, not automatically higher.</strong>
                    What we do not know is left out of the calculation rather than counted as a zero — so a
                    blank leaves part of your position undetermined rather than penalised. Once you fill it
                    in, the real value counts, and a modest real value can move a score down as well as up.
                    What you get is a position that reflects your business instead of a gap.
                  </p>

                  <ol class="fl-score-help__list">
                    <li v-for="lever of scoreLevers" :key="lever.title">
                      <div>
                        <b>{{ lever.title }}</b>
                        <span>{{ lever.body }}</span>
                      </div>
                      <button v-if="lever.step !== null" @click="openEditor(lever.step)">
                        {{ lever.action }} <span aria-hidden="true">↗</span>
                      </button>
                    </li>
                  </ol>

                  <div class="fl-score-help__highlight">
                    <b>The biggest lever is not in this form.</b>
                    <p>
                      Your impact score for each transition is assessed from your <em>public</em> website and
                      press coverage — not from what you write here. Concrete evidence scores well: named
                      customers, volumes, the share of revenue a transition represents, independent coverage.
                      General sustainability language does not, unless someone independent backs it up.
                    </p>
                  </div>

                  <p class="fl-score-help__caveat">
                    Some things are not scored yet, and nothing you enter today changes them: sourcing and
                    supply reliability, regulatory complexity, and how much of a bottleneck you solve. We
                    would rather say so than let you fill in a form that goes nowhere.
                  </p>
                </details>-->
      </section>

      <!--
        ─────────── 03 Connections ───────────

        Replaces "02 / Position in the ecosystem", which listed the actor's category, activities and
        memberships — every one of which already appears elsewhere on the page (the category in the
        hero facts band and again in the technology path, the activities and memberships in section
        04). It restated the profile instead of adding to it. Actual relationships do add something:
        who this organisation works with, and as what.
      -->
      <section class="fl-section fl-connections" v-if="showConnectionsSection" ref="connections">
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">{{ sectionNumbers.connections }} / Connections</span>
            <!--
              The one section the wizard does not cover: relationships are records between two
              actors rather than profile fields, so this opens the connections side panel — the same
              one the classic and simplified profiles use.
            -->
            <button class="fl-section-edit" v-if="showOwnerTools" @click="openConnectionsPanel">
              Edit connections
            </button>
            <h2>From connection to<br>collective impact.</h2>
          </div>
          <p>
            The relationships and collaborations that help {{ actor.name }} turn ambition into impact.
          </p>
        </div>

        <div class="fl-connection-grid">
          <div class="fl-connection-group" v-for="group of connectionGroups" :key="group.key">
            <div class="fl-card-head">
              <span>{{ group.label }}</span><em>{{ group.total }}</em>
            </div>
            <a
              v-for="item of group.items"
              :key="'connection' + item.type + item.id"
              @click="handleClickActorCard(item, 'Connection')"
            >
              <!-- Same resolver as the actor's own logo (logoUrl → actorLogo): a bare path, a
                   logo.dev lookup, or initials when neither yields anything. -->
              <span
                class="fl-connection-logo"
                v-if="item.logo"
                :style="{ backgroundImage: `url(${item.logo})` }"
                aria-hidden="true"
              ></span>
              <span class="fl-connection-logo" v-else aria-hidden="true">{{ item.initials }}</span>
              <b>{{ item.name }}</b>
              <span class="fl-connection-meta" v-if="item.meta">{{ item.meta }}</span>
              <i>↗</i>
            </a>
            <!-- Named rather than silently dropped: a truncated list that looks complete is worse
                 than a short one that says so. -->
            <small v-if="group.total > group.items.length">
              +{{ group.total - group.items.length }} more
            </small>
          </div>
        </div>
      </section>

      <!-- ─────────── 04 Technology footprint ─────────── -->
      <section
        class="fl-section fl-technology"
        v-if="showTechnologySection"
        ref="technology"
      >
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">{{ sectionNumbers.technology }} / Offering</span>
            <button class="fl-section-edit" v-if="showOwnerTools" @click="openEditor(4, 'Edit offering')">
              Edit offering
            </button>
            <h2>Products, capabilities<br>and technology</h2>
          </div>
          <p>
            What {{ actor.name }} offers, the capabilities it brings and the technologies it works with
          </p>
        </div>

        <!--
          The classic profile's long-form "Offering" text (`additional_information`). It opens this
          section rather than section 01: "what you offer" is the question this section asks, and
          the prose is the actor's own answer to it — the taxonomy rows below are ours.

          Members and the actor's own team only, per the UX matrix (visitor: NA), even though the
          rows underneath are public.
        -->
        <div class="fl-offering" v-if="hasOfferingDescription">
          <h4>{{ $t('actor_detail_offering') }}</h4>
          <RichBodyContent class="fl-copy" :html="localizedOfferingDescription"/>
          <div class="fl-readmore" v-if="offeringDescriptionIsTruncatable">
            <button @click="toggleOfferingDescription">
              {{ showFullOfferingDescription ? 'Read less' : 'Read more' }}
            </button>
          </div>
        </div>

        <!--
          Owner-only, and the same bargain the products card makes: nothing to read here, but this
          section is the only way into the field that fills it, so it stands rather than leaving the
          control unreachable on a profile that has nothing yet.
        -->
        <div class="fl-offering" v-else-if="showOwnerTools">
          <h4>{{ $t('actor_detail_offering') }}</h4>
          <p class="fl-offering__empty">
            Nothing on record yet — “Edit offering” opens the field that fills this in.
          </p>
        </div>

        <!--
          The whole bar is dropped rather than printed as "0 mapped capabilities" when the section is
          carried by the offering prose alone: a zero reads as a defect on a profile that has
          something to say, and the bar has a white background of its own, so keeping it for an
          absent count left an empty strip under the prose.
        -->
        <!--        <div class="fl-actor-path" v-if="mappedCapabilityCount">
                  <i>{{ mappedCapabilityCount }} mapped capabilities</i>
                </div>-->

        <div class="fl-tech-grid" v-if="mappedCapabilityCount">
          <div class="fl-tech-card fl-tech-card--main" v-if="technologyRows.length">
            <div class="fl-card-head">
              <span>{{ technologyTitle }}</span><em>Capability</em>
            </div>
            <div class="fl-tech-row" v-for="row of technologyRows" :key="'tech' + row.id">
              <b>{{ row.name }}</b>
              <span>{{ row.category || '—' }}</span>
            </div>
          </div>
          <div class="fl-tech-card fl-tech-card--muted" v-if="domainRows.length">
            <div class="fl-card-head">
              <span>{{ domainsTitle }}</span><em>Focus</em>
            </div>
            <div class="fl-tech-row" v-for="row of domainRows" :key="'domain' + row.id">
              <b>{{ row.name }}</b>
              <span>{{ row.category || '—' }}</span>
              <em v-if="row.offers">{{ row.offers }}</em>
            </div>
          </div>
        </div>
      </section>

      <!-- ─────────── 05 Needs, offers & match potential ─────────── -->
      <section
        class="fl-section fl-opportunities"
        v-if="showOpportunitiesSection"
        ref="opportunities"
      >
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">{{ sectionNumbers.opportunities }} / Needs, offers &amp; match potential</span>
            <button class="fl-section-edit" v-if="showOwnerTools" @click="openEditor(6, 'Edit needs & offers')">
              Edit needs &amp; offers
            </button>
            <h2>Make the next<br>connection count.</h2>
          </div>
          <p>
            Matches become more useful when actors clearly state what they need, what they can
            offer and when they want to act.
          </p>
        </div>

        <div class="fl-needs-grid" v-if="seekingItems.length || offeringItems.length">
          <div class="fl-need-card fl-need-card--needs">
            <span>We are looking for</span>
            <h3 v-for="need of seekingItems" :key="'need' + need">→ {{ need }}</h3>
            <p v-if="!seekingItems.length">Nothing on record yet.</p>
          </div>
          <div class="fl-need-card fl-need-card--offers">
            <span>We can offer</span>
            <h3 v-for="offer of offeringItems" :key="'offer' + offer">→ {{ offer }}</h3>
            <p v-if="!offeringItems.length">Nothing on record yet.</p>
          </div>
        </div>

        <div class="fl-match-row" v-if="matchCards.length">
          <article v-for="(card, index) of matchCards" :key="'match' + index">
            <span>{{ card.kind }}</span>
            <h3>{{ card.title }}</h3>
            <p v-if="card.body">{{ card.body }}</p>
            <p v-else-if="card.meta.length">{{ card.meta.join(' · ') }}</p>
            <a v-if="showContactsSection" @click="scrollToSection('contacts', 'Match card')">
              {{ $t('conversation_start') }} ↗
            </a>
          </article>
        </div>
      </section>

      <!-- ─────────── 06 Join, for anyone whose organisation is not on the map yet ─────────── -->
      <section class="fl-section bg-[var(--fl-sand)] text-center" v-if="showJoinCta" ref="join">
        <div class="max-w-[620px] mx-auto [&_h2]:text-[38px] [&_h2]:leading-[1.08]
                    [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-5
                    [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-ink)]">
          <span class="fl-section-no">{{ sectionNumbers.join }} / Join Foodleap</span>
          <h2>Put your organisation on the transition map.</h2>
          <p>
            {{ actor.name }} is mapped across the food transitions. Add your own organisation and
            become findable by the people looking for exactly what you do.
          </p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="goToRegister('Join')">
              {{ isLoggedIn ? 'Add my organisation' : 'Register & add my organisation' }}
              <span aria-hidden="true">↗</span>
            </button>
            <button class="fl-textbtn" v-if="!isLoggedIn" @click.prevent="showSignIn('Join')">
              Already a member? Sign in
            </button>
          </div>
        </div>
      </section>

      <!-- ─────────── 06 Contacts ─────────── -->
      <section class="fl-section fl-contacts" v-if="showContactsSection" ref="contacts">
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">{{ sectionNumbers.contacts }} / Get in touch</span>
            <h2>Talk to the people<br>behind the profile.</h2>
          </div>
          <p v-if="hasContacts">Reach out directly to the contacts who represent {{ actor.name }} on Foodleap.</p>
        </div>

        <!--
          The actor's own team only, per the UX matrix: an ecosystem member who wants to manage
          someone else's contributors does that from the classic profile. The modal reads the actor
          from the route and reports back over `updateContributors`, which this page already
          listens for — so no context has to be handed to it.
        -->
        <!-- The design's bordered white control rather than the page's own text button: it sits
             above a row of white cards now, and the old ghost button read as part of the copy. -->
        <button
          class="tw-flex items-center gap-2 mt-7 rounded-lg border border-[var(--fl-line)] bg-white
                 px-4 py-3 font-[inherit] text-[13px] font-medium text-[var(--fl-ink)] cursor-pointer
                 transition-colors hover:bg-[var(--fl-pale)] focus-visible:outline-2
                 focus-visible:outline-offset-2 focus-visible:outline-[var(--fl-green)]"
          type="button"
          v-if="isActorTeam"
          @click="openContributorModal('Contacts')"
        >
          <icon name="users" size="16"/>
          {{ $t('actor_view_contributors') }}
        </button>
        <p class="text-[12px] text-[var(--fl-muted)]" v-if="contributors.loading">{{ $t('loading') }}</p>
        <!-- Built here rather than with ActorDetailContributors: that component carries the
             admin affordances (contactable toggle, remove, communities) which have no place on a
             public-facing profile, and its card styling belongs to the standard actor pages. -->
        <!--
          A scrolling row of person tiles rather than a grid of cards: the tile is the whole
          control — it opens the contributor drawer (`user-information-side-panel`), which is where
          the bio, the full expertise list and the conversation action live. Built here rather than
          with ActorDetailContributors for the same reason as before: that component carries the
          admin affordances, which have no place on an outward-facing profile.
        -->
        <div
          class="tw-flex gap-8 items-start mt-12 -mx-1 px-1 py-1 pb-4 overflow-x-auto"
          v-else-if="hasContacts"
        >
          <div
            class="shrink-0 w-[180px]"
            v-for="contact of contributors.list"
            :key="'contact' + contact.user_id"
          >
            <!--
              Every slot below carries a reserved height rather than a min-height, and each text
              slot is clamped to the number of lines that height fits. A contributor with no bio
              written, no function set, no department to tag, or a name that wraps to two lines
              then produces a card exactly as tall as everyone else's — which is what a row of
              tiles of one fixed width needs in order to look deliberate.
            -->
            <button
              class="w-full rounded-xl border p-5 text-center font-[inherit] cursor-pointer
                     transition-colors focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-[var(--fl-green)] motion-reduce:transition-none"
              :class="openContactUserId === contact.user_id
                ? 'bg-white border-[var(--fl-green)] shadow-sm'
                : 'bg-transparent border-transparent hover:bg-white/60'"
              type="button"
              :aria-pressed="openContactUserId === contact.user_id ? 'true' : 'false'"
              @click="openUserPanel(contact)"
            >
              <!-- Most contributors have no photo, so the initials fill is the common case rather
                   than the fallback. -->
              <span
                class="tw-flex items-center justify-center w-24 h-24 mx-auto rounded-full
                       bg-[var(--fl-mint)] bg-cover bg-center text-[26px] font-semibold
                       text-[var(--fl-ink)]"
                :style="contact.photo_url ? { backgroundImage: `url(${contact.photo_url})` } : null"
                aria-hidden="true"
              >{{ contact.photo_url ? '' : contactInitials(contact) }}</span>

              <!-- Two lines' worth at leading-tight; longer names clamp rather than grow the card. -->
              <span
                class="mt-4 h-[38px] line-clamp-2 break-words text-[15px] font-semibold
                       leading-tight text-[var(--fl-ink)]"
                :title="contact.user_name"
              >{{ contact.user_name }}</span>

              <!--
                The first two lines of the person's own words, directly under their name: who
                someone is, before what they are filed under. The whole text is one tap away in
                the drawer this tile opens. Reserved whether or not there is a bio to fill it,
                like every other slot here.
              -->
              <span
                class="mt-2 h-9 line-clamp-2 break-words text-[12px] leading-[18px]
                       text-[var(--fl-muted)]"
                :title="contactBio(contact)"
              >{{ contactBio(contact) }}</span>

              <!-- Two lines at leading-5. Reserved even when there is no role to put in it. -->
              <!--              <span
                              class="mt-2 h-10 line-clamp-2 break-words text-[13px] leading-5
                                     text-[var(&#45;&#45;fl-muted)]"
                              :title="contactRole(contact)"
                            >{{ contactRole(contact) }}</span>-->

              <!--
                One row of chips, reserved whether or not there is a tag to fill it. No wrapping and
                a truncating chip: the tile's content box is 108px, and a department like "Research
                & Development" is wider than that — wrapped, it fell onto a second line that this
                reserved height then cut through mid-glyph.
              -->
              <span class="tw-flex items-center justify-center gap-2 mt-4 h-[26px] overflow-hidden">
                <span
                  class="max-w-full truncate rounded-md bg-[var(--fl-pale)] px-2 py-1 text-[11px]
                         leading-[16px] text-[var(--fl-green-ink)]"
                  v-for="tag of contactTags(contact)"
                  :key="tag"
                  :title="tag"
                >{{ tag }}</span>
              </span>
            </button>

            <span
              class="block mt-2 text-center text-[11px] text-[var(--fl-muted)]"
              v-if="contact.user_id === $store.getters.userId"
            >This is you</span>
          </div>
        </div>

        <!-- Who these people are and who may change the list — the footnote the design closes the
             section with. The actor's own team only: a visitor cannot manage anyone. -->
        <div
          class="tw-flex items-center justify-center gap-5 max-w-[720px] mx-auto mt-12 rounded-xl
                 border border-[var(--fl-line)] bg-white px-6 py-5"
          v-if="isActorTeam && hasContacts"
        >
          <span
            class="shrink-0 tw-flex items-center justify-center w-12 h-12 rounded-full
                   bg-[var(--fl-mint)] text-[var(--fl-green-ink)]"
            aria-hidden="true"
          >
            <icon name="users" size="18"/>
          </span>
          <p class="m-0 text-center text-[13px] leading-6 text-[var(--fl-muted)]">
            These are the contributors representing {{ actor.name }} on Foodleap.
            <br>
            Only organisation admins can manage people and permissions.
          </p>
        </div>
      </section>

      <!--
        ─────────── Testimonials ───────────

        After the people, not before: the contacts are who you talk to, the testimonials are what
        others say about them, and the second only means something once you know the first.

        Read-only and members-only. Written here rather than reusing SimplifiedActorDetailTestimonials
        for the same reason as the contributor cards above — that component carries the tabs, the
        write/edit/hide affordances and the classic page's styling, none of which belong on an
        outward-facing profile. Writing one is still done from the classic profile.
      -->
      <section class="fl-section fl-testimonials" v-if="showTestimonialsSection" ref="testimonials">
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">{{ sectionNumbers.testimonials }} / {{ $t('testimonials_title') }}</span>
            <h2>What others<br>say.</h2>
          </div>
          <p>Testimonials written by organisations {{ actor.name }} has worked with.</p>
        </div>

        <p class="text-[12px] text-[var(--fl-muted)]" v-if="testimonials.loading">{{ $t('loading') }}</p>
        <div class="fl-testimonial-grid" v-else>
          <article v-for="testimonial of testimonials.list" :key="'testimonial' + testimonial.id">
            <div class="fl-testimonial-rating" v-if="testimonial.rating" :aria-label="testimonial.rating + '/5'">
              <span v-for="star of 5" :key="star" :class="{ 'is-on': star <= testimonial.rating }">★</span>
            </div>
            <blockquote>{{ testimonial.content }}</blockquote>
            <footer>
              <b v-if="testimonial.authorActor">{{ testimonial.authorActor.name }}</b>
              <span v-if="testimonial.user">{{ testimonial.user.name }}</span>
              <em v-if="testimonial.relationshipDescription">{{ testimonial.relationshipDescription }}</em>
            </footer>
          </article>
        </div>
      </section>

      <!-- ─────────── Closing ─────────── -->
      <section class="fl-closing">
        <!-- Owners are asked to complete the profile; visitors are given a way to act on it. -->
        <template v-if="canUpgrade">
          <h2>Your place in accelerating the food transition deserves to be seen.</h2>
          <p>
            A complete profile makes your contribution visible—and your next opportunity easier
            to find.
          </p>
        </template>
        <template v-else>
          <h2 class="fl-closing__title--actor">Work with {{ actor.name }}.</h2>
          <p>
            Foodleap maps how this organisation contributes to the food transition—so the next
            collaboration is easier to find.
          </p>
        </template>

        <div class="fl-closing__actions">
          <button
            class="fl-btn fl-btn--primary"
            v-if="showOwnerTools"
            @click="openEditor(0, 'Complete your profile')"
          >
            Complete your profile <span aria-hidden="true">↗</span>
          </button>
          <button
            class="fl-btn"
            :class="showOwnerTools ? 'fl-btn--ghost' : 'fl-btn--primary'"
            v-if="showClaimCta"
            @click="onClickClaimActor('Closing')"
          >Claim &amp; enrich this profile <span aria-hidden="true">↗</span>
          </button>
          <button
            class="fl-btn fl-btn--primary"
            v-if="!canUpgrade && visitorAction"
            @click="scrollToSection(visitorAction.section, 'Closing')"
          >{{ visitorAction.label }} <span aria-hidden="true">↗</span></button>
          <button class="fl-btn fl-btn--ghost" @click="shareProfile('Closing')">Share profile</button>
        </div>
      </section>

      <!-- ─────────── More profiles ─────────── -->
      <section class="fl-more" v-if="nextActors.length" ref="more">
        <span class="fl-section-no">{{ $t('more_actor_profiles') }}</span>
        <div class="fl-more__grid">
          <simplified-image-card
            v-for="similarActor of nextActors.slice(0, 3)"
            :key="similarActor.id"
            :full-size="true"
            :name="similarActor.name"
            :sub-title="nextActorDescription(similarActor)"
            :image="similarActor.featured_image_url || similarActor.website_screenshot || getDefaultFallbackImageUrl()"
            @click="handleClickActorCard(similarActor, 'Similar actor')"
          />
        </div>
      </section>

    </template>

    <!-- No profile to show: loading, a claim in review, not signed in, or no access. Written in the
         page's own language rather than the generic actor-detail empty state, because for an
         anonymous visitor this screen *is* the product's first impression. -->
    <template v-else>
      <div class="tw-flex items-center justify-center min-h-[62vh] py-[90px] px-[7vw]
                  bg-[var(--fl-sand)] text-center">
        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-if="loading">
          <span class="fl-overline">Foodleap</span>
          <h2>{{ $t('loading') }}</h2>
        </div>

        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-else-if="actorClaimInProgress">
          <span class="fl-overline">Claim in review</span>
          <h2>Your claim is on its way.</h2>
          <p>{{ $t('actor_error_claim_processing') }}</p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="$router.push('/')">
              {{ $t('go_to_homepage') }}
            </button>
          </div>
        </div>

        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-else-if="!isLoggedIn">
          <span class="fl-overline">Foodleap</span>
          <h2>See how organisations are driving the food transition.</h2>
          <p>{{ $t('actor_error_login_required') }}</p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="goToRegister('Login gate')">
              {{ $t('action_register') }} &amp; add my organisation <span aria-hidden="true">↗</span>
            </button>
            <button class="fl-textbtn" @click.prevent="showSignIn('Login gate')">Already a member? Sign in</button>
          </div>
        </div>

        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-else>
          <span class="fl-overline">No access</span>
          <h2>This profile is not open to you.</h2>
          <p>{{ $t('actor_error_permissions') }}</p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="$router.push('/')">
              {{ $t('go_to_homepage') }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'

  import RichBodyContent from '@/components/RichBodyContent/RichBodyContent.vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'

  import CompanyMixin from '@/util/CompanyMixin.js'
  import ConfigMixin from '@/util/ConfigMixin.js'
  import TranslationsMixin from '@/util/TranslationsMixin.js'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin.ts'
  import UiMixin from '@/util/UiMixin.js'
  import UserMixin from '@/util/UserMixin'

  import MODAL_IDS from '@/constants/modal-ids.js'
  import { ACTION_TYPES as ACTORS_ACTION_TYPES, MUTATION_TYPES as ACTORS_MUTATION_TYPES } from '@/store/modules/actors.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui.js'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '@/store/modules/filters.js'
  import { COUNTRIES, COUNTRIES_EUROSTAT, countryToOption } from '@/constants/countries.js'
  import { sustainabilityGoals, sustainabilityGoalsNL } from '@/constants/config.js'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import { Conversations } from '@/api/conversations'
  import { fetchActorTechnologyAdoptions } from '@/api/actor-technology-adoptions.js'
  import { fetchActorLocations } from '@/api/actor-locations.js'
  import { fetchActorTransitions } from '@/api/actor-transitions.js'
  import { fetchContributors } from '@/api/actors.js'
  import { getTestimonialsByActor } from '@/Domain/Actor/Api/testimonials.js'
  import { fetchInnovationRadar, fetchInnovationRadarQuarters } from '@/api/innovation-radar'
  import { fetchTransitionPulse, fetchTransitionPulseMonths } from '@/api/transition-pulse'
  import { getConnectionsForActor, htmlText, nl2brUnlessHtml, truncate, truncateHtml } from '@/util/helpers.js'
  import { trackHeapEvent } from '@/util/analytics.js'
  import {
    createScrollDepthTracker,
    trackFoodleapActorVisit,
    trackFoodleapClick,
    trackFoodleapCta,
    trackFoodleapReadMore,
    trackFoodleapSection,
  } from '@/util/useFoodleapTracking.js'

  /**
   * Country name → ISO code, so a market recorded as "United Kingdom" and one recorded as `GB`
   * are recognised as the same place. Both spellings of a code are in here: the eurostat map is
   * merged over the main one, and each contributes its own name for the codes they disagree on
   * ("Great Britain" and "United Kingdom" both resolve to GB).
   */
  const COUNTRY_CODE_BY_NAME = Object.entries({ ...COUNTRIES, ...COUNTRIES_EUROSTAT })
    .concat(Object.entries(COUNTRIES))
    .reduce((codes, [code, name]) => {
      codes[name.toLowerCase()] = code

      return codes
    }, {})

  /**
   * Matomo page name, prefixing every event this page sends (see useFoodleapTracking). One name for
   * every actor: which profile was being read is already the page-view URL, and putting the actor
   * in the event name too would split each of these counts across thousands of rows.
   */
  const TRACKING_PAGE = 'Actor Profile'

  const DESCRIPTION_LIMIT = 1000
  const AMOUNT_OF_ACTORS_IN_NEXT_ACTORS_SECTION = 3
  // Three lines' worth in a card of this width, which is where the card's own clamp cuts anyway.
  const NEXT_ACTOR_DESCRIPTION_LIMIT = 160
  // Enough to show the shape of a relationship type; the group header carries the real total.
  const CONNECTIONS_PER_GROUP = 8
  // Main domains shown in the expertise panel, and subdomains shown under each of them.
  const EXPERTISE_DOMAINS = 5
  const EXPERTISE_SUBDOMAINS_PER_DOMAIN = 4

  // Augmented-profile palette — see the Foodleap design source (actor-profile).
  const ACCENT_ORANGE = '#ef9d47'
  const ACCENT_GREEN = '#78ad46'
  const ACCENT_BLUE = '#7eb5df'
  const ACCENT_CORAL = '#df6e63'

  /**
   * The four Foodleap food transitions, keyed exactly as the transition pulse report stores
   * them. Colours match the transition tabs in the augmented-profile design.
   */
  const TRANSITIONS = {
    protein: { label: 'Protein shift', color: ACCENT_ORANGE },
    circular: { label: 'Circular food system', color: ACCENT_GREEN },
    health: { label: 'Healthy & sustainable food', color: ACCENT_BLUE },
    upcycling: { label: 'Food upcycling', color: ACCENT_CORAL },
  }

  const UPGRADE_REASONS = [
    { number: '01', title: 'Be understood', body: 'Show your role and evidence across four transitions.' },
    { number: '02', title: 'Be discovered', body: 'Make capabilities, products and technologies searchable.' },
    { number: '03', title: 'Be connected', body: 'Match your needs and offers with relevant actors.' },
  ]

  /**
   * What an actor can change, and what it actually affects — the user-facing half of the scoring
   * notes above. `step` is the upgrade-wizard step that edits it, or null when it is not editable
   * from the profile at all.
   *
   * Every claim here is deliberately hedged to what the current model really does. Nothing in this
   * list promises a higher number; the honest promise is that a filled-in field lets a real number
   * replace an undetermined one.
   */
  const SCORE_LEVERS = [
    {
      title: 'Your category',
      body: 'Sets how central you are assumed to be in the ecosystem, which drives the size of your ' +
        'dot on the radar. Pick the one that describes you most accurately — a network or ' +
        'association sits at one end of that scale, an operating company at the other.',
      action: 'Check your category',
      step: 2,
    },
    {
      title: 'Team size',
      body: 'Counts towards how established your business looks. We read the top of the range you ' +
        'give, so keep it current as you grow.',
      action: 'Update headcount',
      step: 2,
    },
    {
      title: 'Your technology, and how far you have taken it',
      body: 'Your most mature technology sets your readiness — one accurate "deployed" or "scaled" ' +
        'entry is enough, and adding less mature ones alongside it does not dilute that. A ' +
        'technology with no status set is skipped entirely, so a mature product left blank counts ' +
        'for nothing at all.',
      action: 'Set adoption status',
      step: 4,
    },
    {
      title: 'The domains you work in',
      body: 'Guarantees a minimum score on climate, waste and health impact. It cannot reach the top ' +
        'of that range on its own — that needs measured figures such as tonnes of CO₂ or the share ' +
        'of waste diverted, and there is no way to submit those yet.',
      action: 'Review your domains',
      step: 6,
    },
    {
      title: 'Your role in each transition',
      body: 'Two things happen here, and neither is the score itself. Your ambition, activities and ' +
        'outcomes are the story shown next to the number, so a reader sees your reasoning rather ' +
        'than a bare figure. And once you add at least one piece of evidence alongside them, your ' +
        'own account of which transitions you belong to takes precedence over our automated reading ' +
        'of your business — without evidence, ours stands. If the score itself looks wrong, it is ' +
        'the public evidence below that changes it, not this form.',
      action: 'Describe your role',
      step: 3,
    },
    {
      title: 'Revenue, funding, customers and partners',
      body: 'These come from our records rather than this form, and they matter: revenue and funding ' +
        'move your readiness, while recorded customers and partners move both your readiness and ' +
        'the size of your dot. If any of it is out of date or missing, tell your Foodleap contact.',
      action: null,
      step: null,
    },
  ]

  /* ═══════════════════════════════════════════════════════════════════════════════════════════
   * WHICH PROFILE CHANGES MOVE AN ACTOR'S RADAR SCORE — AND WHICH DO NOT
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   *
   * This page renders the Innovation Radar v2 scores (readiness / impact / connectedness and the
   * quadrant). Below is the mapping from "what an actor edits here" to "what that actually moves",
   * so nobody writes a tooltip or an onboarding email promising something the scorer ignores.
   *
   * Sources — re-diff against these before writing user copy, they are a snapshot not a spec:
   *   · Explainer:   https://datascouts.atlassian.net/wiki/spaces/EcosystemTech/pages/4898783237
   *   · Field guide: https://datascouts.atlassian.net/wiki/spaces/EcosystemTech/pages/4899405827
   *   · IR v2 is a first-pass POC validated on a 20-actor batch, as of 2026-08-04.
   *
   * ── THE ONE RULE TO GET RIGHT ───────────────────────────────────────────────────────────────
   *
   * Filling something in makes a score ACCURATE. It does not guarantee a HIGHER one, and it can
   * make it lower. Two different mechanics get conflated here, so keep them apart:
   *
   *   · A blank is not punished. A criterion with no usable data is excluded from its group's
   *     average rather than scored zero. That is the part that is safe to promise.
   *   · A filled-in field IS scored. Once a real value enters an average it was previously absent
   *     from, a modest value pulls that average down. One criterion scoring 5 with the rest blank
   *     averages 5; add a real 1 next to it and the group is 3.
   *
   * Concrete ways filling something in lowers a score:
   *   · Setting a blank adoption status to "exploring" scores 1.0 where nothing was counted before.
   *   · Setting a category that carries a 0 orchestration prior (most operating companies) where
   *     the category was previously unknown.
   *   · Publishing more about yourself without evidence: transition alignment caps self-reported
   *     claims at 2, and scores a diversified business LOWER than a pure-play serving the same
   *     transition. A broader public story can therefore reduce it.
   *
   *   Say:     "this makes your position reflect your business instead of a gap"
   *   Not:     "filling this in never lowers your score"   ← false, and a promise we would break
   *   Not:     "blank fields hurt your score"              ← also false; blanks are excluded
   *
   * ── WHAT THE ACTOR CAN CHANGE FROM THE UPGRADE WIZARD ───────────────────────────────────────
   *
   * Step 3 · Category (single-select)
   *   Moves:  connectedness (Orchestration dot size), via a fixed category prior.
   *   Detail: Network/association = 4, innovation support / public authority / investor = 3,
   *           research & education / test facility = 2, most operating-company categories = 0.
   *   Say:    pick the single most accurate one. It is the largest single lever on dot size — but
   *           it is a factual classification question, not something to optimise.
   *
   * Step 3 · Employee range
   *   Moves:  readiness, through two separate criteria on two different curves (team strength, and
   *           an averaged "capital + brains" growth score).
   *   Detail: the scorer reads the range's UPPER bound. Bands step at 5 / 20 / 50 / 200 / 1000.
   *   Say:    keep headcount current; the top of the range is what is read.
   *
   * Step 4 · Transitions — relevance and narrative
   *   Moves:  nothing mechanically, and this is the most common misunderstanding on this page.
   *   Detail: the impact axis comes from `transition_alignment`, which is scored by a live web
   *           search over the actor's PUBLIC website — not from what they tick here. Their claims
   *           are read only as a secondary cross-check shown beside the score.
   *   Say:    make the public website state, with evidence, which transition is core to the
   *           business. Vague sustainability language is explicitly capped at 2 unless a third
   *           party corroborates it. Keeping claims consistent with the public story makes the
   *           cross-check read cleanly; it does not move the number.
   *
   * Step 5 · Technology + adoption status
   *   Moves:  readiness, via technology readiness level (TRL).
   *   Detail: the scorer takes the MAX across all tagged technologies —
   *           exploring 1.0 · piloting 2.5 · deployed 4.0 · scaled 5.0.
   *           ⚠ A BLANK status is ignored, not scored 0. A fully mature product with no status set
   *             scores the same as having no technology at all.
   *           ⚠ Our enum also has `roadmap` and `discontinued`. IR v2's table has neither, so they
   *             fall through as "no status" and contribute nothing.
   *   Say:    one accurately-tagged "deployed" or "scaled" technology already secures a strong TRL.
   *           Setting the status on the flagship product is the whole game here.
   *
   * Step 7 · Domains (offered)
   *   Moves:  impact, but only as a FLOOR on CO₂ / waste / health-nutrition criteria.
   *   Detail: floors range 1.0–2.0 depending on the tag; the highest applies, they do not sum.
   *   Say:    accurate tags stop these three from being unscored. They cannot on their own push
   *           them into the strong range — 3+ needs a quantified figure (tonnes CO₂/year, % waste
   *           diverted, clinical data), and there is currently NO channel to submit that.
   *
   * ── WHAT MOVES THE SCORE BUT IS *NOT* EDITABLE HERE ─────────────────────────────────────────
   *
   *   Revenue (operating income) → readiness. Step function with a cliff: <1M = 1.25,
   *     1–10M = 2.5, ≥10M = 5.0. Going 1M → 9M moves nothing; crossing 10M nearly doubles it.
   *   Total funding → readiness. Bands at 500k / 2M / 10M / 50M.
   *   Relationships (customers, distributors, retail, partners) → readiness AND connectedness.
   *     Counted by keyword on the relationship type; bands at 1 / 2–3 / 4+.
   *
   *   ⚠ Relationships are the single biggest gap: the last validation batch had ZERO relationship
   *     data for every actor tested, so this whole group is unscored across the board. They are
   *     also not writable through the external API (see fetchActorProperties()), so the AI stack
   *     cannot populate them either. Anyone promising actors that "adding partners improves your
   *     score" needs a way for them to add partners first.
   *
   * ── WHAT MOVES NOTHING AT ALL (do not promise these) ────────────────────────────────────────
   *
   *   Structurally removed from every formula pending product decisions — not merely unscored:
   *     supply / feedstock reliability · regulatory approval complexity · bottleneck-solving and
   *     value-chain leverage.
   *   Always unscoreable regardless of input: production scale (the taxonomy cannot tell an
   *     actor's own manufacturing capability from tools it merely uses).
   *   Not actor-actionable: consumer acceptance (a population statistic over the whole ecosystem).
   *   Informational only, never scored: value-chain position, and role in value chain beyond
   *     corroborating category.
   *
   * ── KNOWN MISMATCHES WITH OUR OWN DATA MODEL ────────────────────────────────────────────────
   *
   *   1. Transition names must match IR v2 exactly. Its official four are Protein Shift · Food
   *      Upcycling · Healthy and functional food · Regenerative Agriculture, and
   *      resolveTransition() matches on name — so a transition configured under any other wording
   *      is silently skipped on a claim write, or 422s on the scores endpoint. Transitions are
   *      configured per ecosystem in Datalab → Taxonomies; there is no seeded list to fall back on.
   *   2. Scale. IR v2 emits 0–5; actor_transition_scores validates 0–100. Convert before posting,
   *      or pass --score-scale=5 to ds:migrate-radar-to-transition-scores.
   *   3. The uncertainty band (worst / best / uncertainty) has nowhere to be stored — the table
   *      holds only the four numbers and the quadrant.
   * ═══════════════════════════════════════════════════════════════════════════════════════════ */

  export default defineComponent({
    name: 'FoodleapSimplifiedActorDetail',
    components: {
      RichBodyContent,
      SimplifiedFooterFoodleapNew,
      SimplifiedImageCard,
    },
    // TaxonomyMixin resolves the contributor `department` / `function` ids against the ecosystem
    // taxonomies — both are stored on team_users as ids, not labels.
    mixins: [CompanyMixin, ConfigMixin, TranslationsMixin, TaxonomyMixin, UiMixin, UserMixin],
    data () {
      return {
        actor: {},
        contributors: { list: [], loading: false },
        testimonials: { list: [], loading: false },
        showFullDescription: false,
        showFullOfferingDescription: false,
        // Lifts the caps on the expertise panel's domain and subdomain lists.
        expertiseExpanded: false,
        activeSection: 'story',
        // Null rather than 0: "the reader has not picked a transition yet", which is what lets the
        // section open on the primary one - see selectedTransitionIndex.
        activeTransition: null,
        toast: '',
        toastTimeout: null,
        isInitialLoad: false,
        radarPosition: null,
        pulseMentions: [],
        // Transition claims from actor_transitions. Authoritative when present; the pulse/radar
        // path below stays as a fallback for ecosystems with no claims recorded yet.
        actorTransitions: [],
        upgradeReasons: UPGRADE_REASONS,
        scoreLevers: SCORE_LEVERS,
        actorAdoptions: [],
        // The actor's physical sites, merged and deduplicated server-side. Empty until the call
        // returns, which is why `locationCount` keeps its own fallback.
        locations: [],
      }
    },
    computed: {
      company () {
        return this.$store.getters.detailActor
      },
      /**
       * The profile is not always the whole page: the spotting-area and community pages render it
       * under their own hero, whose tab bar is `position: sticky` inside *their* scroller
       * (`.basic-hero__slot`, z-index 19). Two things follow, both handled under `has-parent-page`:
       *
       *   · The profile must not be its own scroll container. Nested scrollers put its sticky top
       *     bar at the top of a box that starts below the fold and left the wheel moving whichever
       *     of the two the pointer happened to be over.
       *   · Its own top bar has to give up sticky and its z-index once it shares a scroller with
       *     the parent's tab bar, which owns the persistent navigation there.
       *
       * `has-parent-page` is the same class the other embeddable pages use for this — Events.vue,
       * Announcements.vue, KnowledgeBaseSimplified.vue, Challenges.vue.
       */
      isEmbeddedInParentPage () {
        return ['spotting-area-detail', 'community-detail'].includes(this.$route.name)
      },
      loading () {
        return this.isInitialLoad && this.$store.state.actors.detail.loading
      },
      isLoggedIn () {
        return this.$store.getters.isLoggedIn
      },
      profile () {
        return this.$store.state.user && this.$store.state.user.profile
      },
      actorClaimInProgress () {
        if (!this.profile || !this.profile.claims) {
          return false
        }

        return this.profile.claims.map(claim => claim.id).includes(this.$route.params.id)
      },
      /**
       * `userCanEdit` is true on every actor for team members and owners, so they would never
       * see the visitor variant of this page. `?preview=visitor` forces it for review.
       *
       * Gated on `canPreviewAsVisitor`, not on the query alone: without that, a preview link handed
       * to an ecosystem member would put them in a view whose only exit — the toggle — is not
       * rendered for them.
       */
      forcedVisitorPreview () {
        return this.canPreviewAsVisitor && this.$route.query.preview === 'visitor'
      },
      /**
       * Ecosystem owners and DataScouts staff — not plain members.
       *
       * It used to be `userCanEdit || userCanClaim`, which is true for every member and owner alike
       * (they can edit every actor), so the toggle sat in the top bar for the whole ecosystem team.
       * An owner runs the ecosystem and has a reason to check what the public sees; a member does
       * not, and a visitor is already looking at it.
       *
       * Deliberately not `showOwnerTools`/`showClaimCta`: the preview switches those off, so the
       * toggle would disappear the moment it was used.
       */
      canPreviewAsVisitor () {
        return this.isOwner || this.$store.getters.isUserOfDataScoutsTeam
      },
      /**
       * DataScouts staff only — `isUserOfDataScoutsTeam` is an `@datascouts.eu` address. Unlike the
       * visitor preview above, this is our own tooling rather than an ecosystem feature, so it stops
       * at us: the endpoints behind it answer 403 for everyone else and this only decides whether to
       * render the controls at all.
       *
       * Hidden in visitor preview too: previewing is for seeing what a visitor sees, and internal
       * tooling is precisely what they do not.
       */
      canRunOperations () {
        return !this.forcedVisitorPreview && this.$store.getters.isUserOfDataScoutsTeam
      },
      /**
       * The actor's SDGs as renderable entries.
       *
       * Stored as strings like "SDG 7: Affordable and clean energy", so the number is parsed back
       * out to find the image. `value` stays the English constant even in Dutch — that is what the
       * `sustainability_goal` facet indexes, so it is what a filter has to be given.
       */
      sustainabilityGoalItems () {
        const isDutch = this.$store.getters.activeLocale === 'nl'
        const labels = isDutch ? sustainabilityGoalsNL : sustainabilityGoals
        const path = isDutch ? '/images/SDG_nl/' : '/images/SDG/'

        return (this.actor.sustainability_goal || [])
          .map(goal => {
            const text = String(goal)

            return Number(text.substring(text.indexOf(' ') + 1, text.indexOf(':')))
          })
          // A goal saved in a format we cannot parse would otherwise ask for /images/SDG/NaN.jpg.
          .filter(number => number >= 1 && number <= sustainabilityGoals.length)
          .sort((a, b) => a - b)
          .map(number => ({
            number,
            value: sustainabilityGoals[number - 1],
            label: labels[number - 1],
            image: `${path}${number}.jpg`,
          }))
      },
      showOwnerTools () {
        return !this.forcedVisitorPreview && this.userCanEdit
      },
      showClaimCta () {
        return !this.forcedVisitorPreview && this.userCanClaim
      },
      /**
       * Who may invite contributors to this actor: its own claimed team, the ecosystem team
       * (members and owners), and anyone else who holds edit rights on this particular actor.
       *
       * Mirrors AddContributorRequest::authorize — the endpoint answers 403 for everyone else, so
       * rendering the button any wider than this would only produce a failed invite:
       *   · `isActorTeam` — a verified actor-owner or actor-contributor claim, i.e. the claimees.
       *   · `isMember` — the ecosystem team; the getter is member *and* owner.
       *   · `userCanEdit` — the remaining way in, a portfolio member with contribute access to a
       *     portfolio this actor sits in. The backend additionally requires the ecosystem's
       *     `allowInviteContributors` permission for that role (`userCanEdit` only checks
       *     portfolio sharing), so it is checked here too rather than letting them 403.
       *
       * Products are excluded the same way userCanClaim excludes them: a product has no team to
       * invite anyone to. And hidden in visitor preview, like every other owner control.
       */
      canInviteContributors () {
        if (!this.actor.id || this.actor.actor_type === 'Product' || this.forcedVisitorPreview) {
          return false
        }

        if (this.isActorTeam || this.isMember) {
          return true
        }

        if (!this.userCanEdit) {
          return false
        }

        return !this.$store.getters.isPortfolioMember ||
          !!this.$store.state.config.allowInviteContributors
      },
      // `logoUrl` comes from CompanyMixin (util/actor.js `actorLogo`): it prefixes bare paths and
      // falls back to a logo.dev lookup on the actor's website.
      resolvedLogo () {
        return this.logoUrl(this.actor)
      },
      // The activation hero speaks to the owner; visitors get the snapshot instead.
      canUpgrade () {
        return this.showOwnerTools || this.showClaimCta
      },
      /**
       * "Why upgrade?" — the actor's own claimed team only.
       *
       * It used to hang off `canUpgrade`, which is true for every ecosystem member and owner on
       * every actor (they can all edit) and for anyone who merely *could* claim. So a member
       * browsing someone else's profile was pitched an eight-minute form for a company they have
       * nothing to do with. The card is addressed to whoever actually has to do the upgrading.
       *
       * Consequence worth knowing: on an unclaimed profile nobody is the claimed team, so everyone
       * — members included — gets the snapshot. Claiming it is what turns the card on.
       */
      showUpgradeCard () {
        return !this.forcedVisitorPreview && this.isActorTeam
      },
      initials () {
        return this.initialsOf(this.actor.name)
      },
      rawDescription () {
        return this.localizedDisplayProperty(this.actor, 'description') || ''
      },
      /**
       * `description` is HTML — written with rich-text-area in both editors — so it is measured and
       * cut as text rather than as source. Counting the markup made the collapsed length depend on
       * how much of it a description carried, and slicing the string cut through tags.
       */
      descriptionIsTruncatable () {
        return htmlText(this.rawDescription).length > DESCRIPTION_LIMIT
      },
      localizedDescription () {
        const html = nl2brUnlessHtml(this.rawDescription)

        if (!this.descriptionIsTruncatable || this.showFullDescription) {
          return html
        }

        return truncateHtml(html, DESCRIPTION_LIMIT)
      },
      ecosystemRelationships () {
        return this.$store.getters.fullActorRelationships
      },
      connections () {
        return getConnectionsForActor(this.actor, this.ecosystemRelationships)
          .filter(connection => connection.name && connection.name.length > 0)
      },
      productItems () {
        return this.connections
          .filter(connection => connection.actorType === 'Product')
          .slice(0, 5)
      },
      subsidiaries () {
        return this.actor.subsidiaries || []
      },
      /**
       * The markets line, as country names.
       *
       * It used to read the head office's `country` and the subsidiaries' `country_code`, so one
       * company came out as "United Kingdom · NL · CN · TR" — a full name followed by a row of
       * codes. Every source stores both (the geocoder and the AI stack each write the English name
       * alongside the ISO code), so the name is what is shown and the code is only a fallback for a
       * row that somehow has no name.
       *
       * Keyed on the ISO code rather than on what is written: the head office records "United
       * Kingdom" for the same place a location row calls `GB`, and the enriched rows often carry a
       * name with no code at all. That is one market, not two or three.
       */
      countries () {
        const addresses = this.locations.length
          ? this.locations
          : [this.actor.address].concat(this.subsidiaries.map(subsidiary => subsidiary.address))

        const byCountry = new Map()

        addresses.filter(Boolean).forEach(address => {
          const name = address.country || (address.country_code ? countryToOption(address.country_code).label : null)

          if (!name) {
            return
          }

          const key = (address.country_code || COUNTRY_CODE_BY_NAME[name.toLowerCase()] || name).toUpperCase()

          if (!byCountry.has(key)) {
            byCountry.set(key, name)
          }
        })

        return [...byCountry.values()]
      },
      employeeSize () {
        const current = this.actor.current_employees && this.actor.current_employees[0]

        if (!current) {
          return null
        }

        if (current.lower_bound === current.upper_bound) {
          return String(current.lower_bound)
        }

        return current.lower_bound + '–' + current.upper_bound
      },
      profileCompleteness () {
        // `completeness` is only exposed to members/owners, so this stays hidden for visitors.
        const completeness = this.actor.completeness

        if (!Number.isFinite(completeness)) {
          return null
        }

        return Math.round(completeness <= 1 ? completeness * 100 : completeness)
      },
      /**
       * The actor's own team: whoever holds a verified claim on this profile, as owner or as
       * contributor.
       *
       * Written out rather than reusing CompanyMixin's `userIsActorOwner` /
       * `userIsActorContributor` or UserMixin's `hasClaimedActor`: all three reach into
       * `state.user.profile.claims` (or `.claimedIds`) without guarding, which throws for an
       * anonymous visitor — and this one is evaluated on every render of a page that visitors see.
       */
      isActorTeam () {
        const claims = (this.profile && this.profile.claims) || []

        return claims.some(claim => claim.id === this.actor.id &&
          claim.claim_verified &&
          ['actor-owner', 'actor-contributor'].includes(claim.claim_type))
      },
      /** The classic profile's long-form "Offering" body. */
      rawOfferingDescription () {
        return this.localizedDisplayProperty(this.actor, 'additional_information') || ''
      },
      offeringDescriptionIsTruncatable () {
        return htmlText(this.rawOfferingDescription).length > DESCRIPTION_LIMIT
      },
      /**
       * Cut at the same measure as the description above it, for the same reason: an upgraded
       * profile's offering runs to several screens of AI prose, and the taxonomy rows underneath it
       * are the rest of the section's answer — they should not be pushed below the fold by it.
       */
      localizedOfferingDescription () {
        const html = nl2brUnlessHtml(this.rawOfferingDescription)

        if (!this.offeringDescriptionIsTruncatable || this.showFullOfferingDescription) {
          return html
        }

        return truncateHtml(html, DESCRIPTION_LIMIT / 2)
      },
      localizedActivitiesDescription () {
        return this.localizedDisplayProperty(this.actor, 'activities_description')
      },
      foundingYear () {
        if (!this.actor.founding_date) {
          return null
        }

        return String(this.actor.founding_date).slice(0, 4)
      },
      originLine () {
        const place = [
          this.actor.address && this.actor.address.city,
          this.actor.address && this.actor.address.country,
        ].filter(Boolean).join(', ')

        return [place, this.foundingYear ? `Founded ${this.foundingYear}` : null]
          .filter(Boolean)
          .join(' · ')
      },
      /**
       * The head office counts as a location. The old expression collapsed to null without
       * subsidiaries, so every single-site organisation showed no location count at all rather than
       * the one it has.
       *
       * `locations` is the whole footprint — head office, subsidiary actors, and the sites the
       * enrichers recorded in the central database, which are on no actor until somebody runs the
       * migration command. It arrives a moment after the actor does, so the local count stays as
       * the fallback: it is the same number minus the central rows, never a contradictory one.
       */
      locationCount () {
        if (this.locations.length) {
          return this.locations.length
        }

        const hasHeadOffice = !!(this.actor.address && (this.actor.address.city || this.actor.address.country))
        const count = this.subsidiaries.length + (hasHeadOffice ? 1 : 0)

        return count || null
      },
      heroStats () {
        return [
          { value: this.employeeSize, label: 'employees' },
          { value: this.locationCount, label: this.locationCount === 1 ? 'location' : 'locations' },
          { value: this.connections.length || null, label: 'connections' },
        ].filter(stat => !!stat.value)
      },
      /**
       * Contacts a reader can actually write to: they have a Foodleap account behind them, and
       * they are not the reader. Messaging yourself is the one thing the contacts section already
       * refuses, so the hero must not offer it either.
       */
      messageableContacts () {
        return (this.contributors.list || [])
          .filter(contact => contact.user_id && contact.user_id !== this.$store.getters.userId)
      },
      /**
       * What a member who does not own this profile can do with it — the hero card the actor's own
       * team uses for the upgrade pitch, put to work for everyone else.
       *
       * Members only: every action here needs an account, and per the no-teaser rule a visitor is
       * shown the snapshot instead rather than three buttons that would only ask them to sign in.
       * Gated on showUpgradeCard rather than isActorTeam so "View as visitor" shows this card,
       * which is the whole point of the preview.
       */
      visitorActions () {
        if (!this.isLoggedIn || this.showUpgradeCard) {
          return []
        }

        const actions = []

        // showContactsSection, not messageableContacts: contributors arrive a moment after the
        // actor, and gating on the loaded list swapped the snapshot card out for this one mid-read.
        if (this.showContactsSection) {
          // `user_name` is what the contributor payload carries — there is no `name` on it, so
          // naming the sole contact printed "Message undefined directly." Still falls back to the
          // generic line when the name is missing: a contributor invited by email has none until
          // they finish registering.
          const soleContact = this.messageableContacts.length === 1
            ? this.messageableContacts[0].user_name
            : null

          actions.push({
            key: 'conversation',
            label: this.$t('conversation_start'),
            note: soleContact
              ? `Message ${soleContact} directly.`
              : `Message whoever represents ${this.actor.name} on Foodleap.`,
            run: this.startConversationWithActor,
          })
        }

        // A testimonial is written by one organisation about another, so the reader needs a claimed
        // actor to write it as. Without one the modal opens straight onto its "no eligible actors"
        // dead end, which is worse than not offering it.
        if (this.nonProductClaims.length) {
          actions.push({
            key: 'testimonial',
            label: 'Write a testimonial',
            note: `Vouch for work you have done with ${this.actor.name}.`,
            run: this.openTestimonialModal,
          })
        }

        // Add to my phone book — held back until the network-contact list is part of this profile.
        // NetworkContactStar is wired for the Samergo network cards and carries their labels
        // (`samergo_network_add_to_wijk`), so dropping it in here would read as another product's
        // control. To turn it on: import { addNetworkContact } from '@/api/network-contacts.js',
        // add an `addToPhoneBook` method around it, and uncomment the push below.
        // actions.push({
        //   key: 'phonebook',
        //   label: 'Add to my phone book',
        //   note: `Keep ${this.actor.name} in your own contacts.`,
        //   run: this.addToPhoneBook,
        // })

        return actions.map((action, index) => ({ ...action, number: this.pad(index + 1) }))
      },
      /** The fact band under the profile heading; the primary transition is highlighted. */
      factItems () {
        const primary = this.primaryTransition

        return [
          { label: 'Company Category', value: this.actor.category && this.actor.category.label },
          { label: 'Company stage', value: (this.actor.stage && this.actor.stage.label) || this.actor.maturity },
          { label: 'Markets', value: this.countries.join(' · ') },
          {
            label: primary && primary.score !== null ? 'Primary transition · highest score' : 'Primary transition',
            value: primary && primary.label,
            score: primary && primary.score,
            primary: true,
          },
        ].filter(item => !!item.value)
      },
      glanceItems () {
        return [
          { value: this.employeeSize, label: 'employees' },
          { value: this.locationCount, label: this.locationCount === 1 ? 'location' : 'locations' },
          { value: this.foundingYear, label: 'founded' },
          { value: this.connections.length || null, label: 'active collaborations' },
        ].filter(item => !!item.value)
      },
      /**
       * An actor's expertise is its industries.
       *
       * Not `expertises`: that taxonomy is user-level — people declare it about themselves via
       * team_user_expertises. The actor_expertises pivot exists but is not what anyone fills in,
       * so reading it here left this section empty on every profile that had industries set.
       */
      expertiseSource () {
        if (this.actor.industries && this.actor.industries.length) {
          return { title: this.getTaxonomyAliasWithDefault('industries'), items: this.actor.industries }
        }

        if (this.actor.business_aspects && this.actor.business_aspects.length) {
          return { title: this.getTaxonomyAliasWithDefault('business_aspects'), items: this.actor.business_aspects }
        }

        return { title: '', items: [] }
      },
      expertiseTitle () {
        return this.expertiseSource.title || 'Domain expertise'
      },
      /**
       * The actor's expertise with its hierarchy intact: each main domain, and the subdomains
       * checked under it listed below it.
       *
       * It used to be a flat list of leaves with the main domain shrunk into a note beside each —
       * so two subdomains of one domain printed that domain's name twice, never as the thing they
       * belong to, and a main domain checked in its own right disappeared the moment any subdomain
       * of it was checked.
       *
       * A main domain heads its group whether or not the actor checked it itself: a subdomain names
       * its parent, and that parent is what makes the subdomain mean anything.
       */
      expertiseDomainGroups () {
        const items = this.expertiseSource.items
        // Industries nest through `industry_id` rather than the `parent_id` / `parentId` the
        // other taxonomies use, so parentIdOf does not see it.
        const parentIdOf = item => this.parentIdOf(item) || item.industry_id || null
        const labelOf = item => item.label || item.name
        const byId = new Map(items.map(item => [String(item.id), item]))
        const groups = new Map()

        const groupFor = (key, label) => {
          if (!groups.has(key)) {
            groups.set(key, { key, label, children: [] })
          }

          return groups.get(key)
        }

        items.forEach(item => {
          const label = labelOf(item)

          if (!label) {
            return
          }

          const parentId = parentIdOf(item)
          const parent = parentId ? byId.get(String(parentId)) : null
          // `parent_name` / `category` carry the main domain's name when the parent itself is not
          // in the actor's list, which is the common case for a checked subdomain.
          const parentLabel = (parent && labelOf(parent)) || item.parent_name || item.category

          // A main domain, or a subdomain whose parent we cannot name — either way it heads its own
          // row rather than being dropped for want of a parent.
          if (!parentId || !parentLabel) {
            groupFor(String(item.id), label)

            return
          }

          groupFor(String(parentId), parentLabel).children.push({
            key: String(parentId) + '-' + String(item.id),
            label,
          })
        })

        // Alphabetical at both levels, and sorted before the caps so which ones show is at least
        // predictable — the order the API returns these in is neither meaningful nor stable.
        const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })

        // Uncapped: the caps belong to `expertiseGroups`, which is the one the toggle lifts them
        // from. Applying them here would leave nothing for it to expand into.
        return [...groups.values()]
          .sort(byLabel)
          .map(group => ({
            ...group,
            children: group.children.sort(byLabel),
          }))
      },
      /**
       * The rows as rendered: capped at five domains and four subdomains each, or complete once
       * the toggle has been used.
       */
      expertiseGroups () {
        const expanded = this.expertiseExpanded
        const groups = expanded
          ? this.expertiseDomainGroups
          : this.expertiseDomainGroups.slice(0, EXPERTISE_DOMAINS)

        return groups.map(group => {
          const children = expanded
            ? group.children
            : group.children.slice(0, EXPERTISE_SUBDOMAINS_PER_DOMAIN)

          return {
            ...group,
            children,
            // Named rather than silently dropped, as everywhere else on this page.
            hidden: group.children.length - children.length,
          }
        })
      },
      /**
       * Whether anything is behind the toggle at all — subdomains count, so a profile with four
       * domains carrying twenty subdomains between them still gets the control.
       */
      hasHiddenExpertise () {
        return this.hiddenExpertiseDomains > 0 ||
          this.expertiseDomainGroups.some(group => group.children.length > EXPERTISE_SUBDOMAINS_PER_DOMAIN)
      },
      /**
       * Names the domains still hidden when there are any, because that is the count the row was
       * already showing; otherwise it can only be subdomains, which are not worth counting in a
       * label the width of this column.
       */
      expertiseToggleLabel () {
        if (this.expertiseExpanded) {
          return 'Show fewer'
        }

        const domains = this.hiddenExpertiseDomains

        if (!domains) {
          return 'Show all expertise'
        }

        return `+${domains} more ${domains === 1 ? 'domain' : 'domains'}`
      },
      /** Same reasoning as the per-domain count: a cut list says so rather than looking complete. */
      hiddenExpertiseDomains () {
        return Math.max(0, this.expertiseDomainGroups.length - EXPERTISE_DOMAINS)
      },
      /**
       * The actor's relationships, grouped by type — "Customer", "Investor", "Partner".
       *
       * Grouped because the type is the only thing that makes a list of company names mean
       * anything, and it is what the old roles section gestured at without ever showing.
       *
       * Products are left out: they already have their own card in section 01, and restating them
       * here would repeat the mistake this section replaces.
       */
      connectionGroups () {
        const groups = new Map()

        this.connections
          .filter(connection => connection.actorType !== 'Product')
          .forEach(connection => {
            // The ecosystem's own word for the relationship first: `alias` is what it set under
            // Manage business relationships, per direction. `label` is only ever the relation's
            // name with the underscores taken out ("has collaboration with"), which is the
            // fallback, and the bare name is the fallback for that.
            const label = connection.alias ||
              connection.label ||
              (connection.type || '').split('_').join(' ')

            if (!label) {
              return
            }

            // Keyed on the relation type, not on what it is called: two relationships an ecosystem
            // has given the same alias are still two relationships.
            const key = connection.type || label

            if (!groups.has(key)) {
              groups.set(key, { key, label, items: [] })
            }

            groups.get(key).items.push({
              id: connection.id,
              name: connection.name,
              type: connection.type,
              logo: this.logoUrl(connection),
              initials: this.initialsOf(connection.name),
              // Whatever we can say about them in one line, in descending order of usefulness.
              meta: this.connectionMeta(connection),
            })
          })

        return [...groups.values()]
          .map(group => ({
            key: group.key,
            label: group.label,
            total: group.items.length,
            items: group.items
              .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
              .slice(0, CONNECTIONS_PER_GROUP),
          }))
          .sort((a, b) => (b.total - a.total) ||
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      },
      /**
       * Members only, per the UX matrix — connections are NA for a visitor there. The count in the
       * hero's at-a-glance card stays public; a total says far less than who they are.
       */
      showConnectionsSection () {
        return this.isLoggedIn && this.connectionGroups.length > 0
      },
      /**
       * One entry per food transition the actor is documented in, merging the innovation radar
       * position (readiness / impact / connectedness) with the transition pulse rationale.
       */
      /** Transitions the actor has actually answered, from actor_transitions. */
      answeredTransitions () {
        return this.actorTransitions.filter(row => row.answered)
      },
      transitionEntries () {
        // Recorded claims win over the report-derived view: they are per-transition, durable and
        // carry their own scores, where the pulse/radar path shares one score across all four.
        if (this.answeredTransitions.length) {
          return this.answeredTransitions
            .filter(row => row.relevance !== 'not_relevant')
            .map(row => ({
              key: String(row.transition.id),
              label: row.transition.name,
              color: row.transition.hex || ACCENT_GREEN,
              contribution: row.ambition || row.activities || null,
              relevance: row.relevance,
              narrative: row,
              role: row.score && row.score.quadrant,
              score: (row.score && row.score.score) ?? null,
              metrics: this.metricsFromScore(row.score),
              source: this.provenanceLine(row),
            }))
        }

        const entries = this.pulseMentions.map(mention => ({
          key: mention.key,
          label: (TRANSITIONS[mention.key] && TRANSITIONS[mention.key].label) || mention.title || mention.key,
          color: (TRANSITIONS[mention.key] && TRANSITIONS[mention.key].color) || ACCENT_GREEN,
          contribution: mention.meta || null,
          ...this.radarFacts,
        }))

        if (entries.length || !this.radarPosition) {
          return entries
        }

        // No pulse mention, but the actor is positioned on the innovation radar.
        return [{
          key: 'radar',
          label: 'Food transition',
          color: ACCENT_GREEN,
          contribution: null,
          ...this.radarFacts,
        }]
      },
      /**
       * What a visitor reads instead of the detail panel: the transitions this actor has actually
       * written an ambition for. Filtered rather than rendered empty — a card whose only content is
       * a transition name it already says on the block above is noise.
       */
      visitorStatements () {
        return this.transitionEntries.filter(entry => !!entry.contribution)
      },
      /**
       * The transition this actor scores highest in - the one the fact band calls out as "Primary
       * transition · highest score".
       *
       * The index rather than the entry, because the tabs are addressed by index and the section
       * opens on this one.
       */
      primaryTransitionIndex () {
        // Reduced rather than sorted so a tie keeps the order the entries arrived in, and so an
        // unscored set (the pulse and radar paths carry no per-transition score) resolves to the
        // first entry instead of whatever the sort happened to leave in front.
        return this.transitionEntries.reduce(
          (best, entry, index) => ((entry.score || 0) > (this.transitionEntries[best].score || 0) ? index : best),
          0,
        )
      },
      primaryTransition () {
        return this.transitionEntries[this.primaryTransitionIndex] || null
      },
      /**
       * The entry the detail panel shows: whichever tab the reader picked, and until they pick one,
       * the primary transition rather than the first tab.
       *
       * The first tab is just the order actor_transitions came back in, which is nothing the reader
       * is told about; the primary one is already named as such in the fact band above, so opening
       * anywhere else made the section contradict the band.
       */
      selectedTransitionIndex () {
        if (this.activeTransition !== null) {
          return this.activeTransition
        }

        return this.primaryTransitionIndex
      },
      radarFacts () {
        const position = this.radarPosition

        if (!position) {
          return { role: null, score: null, metrics: [], source: null }
        }

        const metrics = [
          { label: 'Readiness to scale', value: this.normalizeScore(position.x) },
          { label: 'Strategic impact', value: this.normalizeScore(position.y) },
          { label: 'Connectedness', value: this.normalizeScore(position.orchestrationScore) },
        ].filter(metric => metric.value !== null)

        // The headline score is the average of whichever axes the report actually carries.
        const score = metrics.length
          ? Math.round(metrics.reduce((total, metric) => total + metric.value, 0) / metrics.length)
          : null

        return {
          role: position.label || null,
          score,
          metrics,
          source: position.reportTitle ? `Source: ${position.reportTitle}` : null,
        }
      },
      transition () {
        return this.transitionEntries[this.selectedTransitionIndex] || this.transitionEntries[0] || null
      },
      /** Only the narrative we actually hold — the design's empty cells are left out. */
      narrativeCells () {
        if (!this.transition) {
          return []
        }

        const narrative = this.transition.narrative

        if (narrative) {
          return [
            { label: 'Ambition', value: narrative.ambition },
            { label: 'Activities & practices', value: narrative.activities },
            { label: 'Intended outcomes', value: narrative.intended_outcomes },
            { label: 'Observed outcomes', value: narrative.observed_outcomes },
            { label: 'Evidence', value: this.evidenceLine(narrative) },
          ].filter(cell => !!cell.value)
        }

        return [
          { label: 'Contribution', value: this.transition.contribution },
          { label: 'Quadrant', value: this.transition.role },
          { label: 'Evidence', value: this.transition.source },
        ].filter(cell => !!cell.value)
      },
      /**
       * What the wizard asks about when the actor has no recorded claims yet.
       *
       * Stays on the page rather than in the modal: working it out means reading the transition
       * pulse and the innovation radar, which is this page's job. The modal prefers the claims it is
       * handed and only falls back to this.
       */
      wizardFallbackTransitions () {
        if (this.transitionEntries.length) {
          return this.transitionEntries
        }

        return Object.keys(TRANSITIONS).map(key => ({
          key,
          label: TRANSITIONS[key].label,
          color: TRANSITIONS[key].color,
          contribution: null,
          score: null,
        }))
      },
      technologyTitle () {
        return this.getTaxonomyAliasWithDefault('technology') || 'Technology'
      },
      domainsTitle () {
        return this.getTaxonomyAliasWithDefault('domains') || 'Innovation domains'
      },
      technologyRows () {
        return this.taxonomyRows('technology')
      },
      domainRows () {
        return this.taxonomyRows('domains').map(row => {
          const domain = (this.actor.domains || []).find(item => item.id === row.id)

          return {
            ...row,
            offers: domain && domain.offers && domain.offers.length
              ? domain.offers.join(' · ')
              : null,
          }
        })
      },
      domainNames () {
        return this.domainRows.map(row => row.name).slice(0, 6)
      },
      mappedCapabilityCount () {
        return this.technologyRows.length + this.domainRows.length
      },
      /**
       * The offering prose is members-only (per the UX matrix), so its visibility — not merely its
       * presence on the actor — is what decides whether it can carry the section on its own.
       */
      hasOfferingDescription () {
        return this.isLoggedIn && !!this.localizedOfferingDescription
      },
      /** What the actor stated it is looking for in the ecosystem. */
      seekingItems () {
        return (this.actor.motivations || [])
          .map(motivation => motivation.label || motivation.name)
          .filter(Boolean)
          .slice(0, 5)
      },
      /** Domains and aspects the actor sells a product or a service in. */
      offeringItems () {
        return (this.actor.domains || [])
          .concat(this.actor.aspects || [])
          .filter(item => item && item.name && item.offers && item.offers.length)
          .map(item => item.name)
          .slice(0, 5)
      },
      matchCards () {
        const cards = []
        const offerings = (this.actor.domains || []).concat(this.actor.aspects || [])
        const motivations = this.actor.motivations || []

        // Offered: domains and aspects the actor sells a product or a service in.
        offerings
          .filter(item => item && item.name && ((item.offers && item.offers.length) || item.remark))
          .forEach(item => {
            cards.push({
              kind: 'Offering',
              title: item.name,
              body: item.remark || null,
              meta: (item.offers || []).slice(),
            })
          })

        // Seeking: what the actor stated it is looking for in the ecosystem.
        motivations.forEach(motivation => {
          cards.push({
            kind: 'Seeking',
            title: motivation.label || motivation.name,
            body: null,
            meta: [],
          })
        })

        return cards.filter(card => !!card.title).slice(0, 3)
      },
      hasContacts () {
        return this.contributors.list && this.contributors.list.length > 0
      },
      /**
       * The tile the contributor drawer is currently open on, so the row shows which person is
       * being read. Kept in the store rather than in local state: the drawer is closed from inside
       * itself, and a local copy would leave a tile ringed with nothing beside it.
       */
      openContactUserId () {
        const panel = this.$store.state.ui.sidePanel

        // `selectedComponent`, not `component`: the mutation is given a `component` key but stores
        // it under this one (see SHOW_SIDE_PANEL in store/modules/ui).
        if (!panel || !panel.isVisible || panel.selectedComponent !== 'user-information-side-panel') {
          return null
        }

        return (panel.metaData && panel.metaData.userId) || null
      },
      /**
       * The join pitch replaces the contacts section for anyone whose own organisation is not on
       * the map — anonymous visitors, and signed-in users who have not claimed an actor.
       *
       * Reaching the contacts is the reward for being part of the ecosystem, so it is not what a
       * non-member is shown; and a member who has never put their own organisation up is exactly
       * who this asks.
       */
      showJoinCta () {
        return !this.isLoggedIn || this.nonProductClaims.length === 0
      },
      /**
       * Every way to reach the actor, as one uniform list.
       *
       * Built here rather than reusing CardInfoSocial because that component sizes each icon
       * individually (18px for LinkedIn, 14px for Twitter) with per-icon margin nudges, so the row
       * never lines up. One size, one shape, one hover state — and the order is deliberate:
       * the channels a visitor is most likely to use come first.
       */
      socialLinks () {
        const actor = this.actor
        const links = [
          { name: 'website', icon: 'world', href: actor.url, title: 'Website' },
          { name: 'email', icon: 'email', href: actor.email ? `mailto:${actor.email}` : null, title: actor.email, external: false },
          { name: 'phone', icon: 'phone', href: actor.phone ? `tel:${actor.phone}` : null, title: actor.phone, external: false },
          { name: 'linkedin', icon: 'linkedin-logo', href: actor.linkedin, title: 'LinkedIn' },
          { name: 'twitter', icon: 'twitter-logo', href: actor.twitter, title: 'X / Twitter' },
          { name: 'facebook', icon: 'facebook-logo', href: actor.facebook, title: 'Facebook' },
          { name: 'instagram', icon: 'instagram-logo', href: actor.instagram, title: 'Instagram' },
          { name: 'youtube', icon: 'youtube-play', href: actor.youtube, title: 'YouTube' },
          { name: 'vimeo', icon: 'vimeo-logo', href: actor.vimeo, title: 'Vimeo' },
          { name: 'crunchbase', icon: 'crunchbase-logo-letters', href: actor.crunchbase, title: 'Crunchbase' },
          { name: 'wikipedia', icon: 'wikipedia-logo', href: actor.wikipedia, title: 'Wikipedia' },
          { name: 'rss', icon: 'rss', href: actor.rss_feed, title: 'RSS' },
        ]

        return links
          // Social fields can arrive as an array of handles; the first is the canonical one.
          .map(link => ({
            ...link,
            href: Array.isArray(link.href) ? link.href[0] : link.href,
            external: link.external !== false,
          }))
          .filter(link => !!link.href)
      },
      /**
       * The website is the actor's public front door, so it stays open to everyone.
       *
       * Everything else in the row — email, phone and the social accounts — is a way to reach the
       * organisation, which is what an anonymous visitor gets by signing in. Splitting rather than
       * filtering in the template so the count in the gate label and the icons behind it cannot
       * disagree.
       */
      visibleSocialLinks () {
        if (this.isLoggedIn) {
          return this.socialLinks
        }

        return this.socialLinks.filter(link => link.name === 'website')
      },
      lockedSocialLinks () {
        if (this.isLoggedIn) {
          return []
        }

        return this.socialLinks.filter(link => link.name !== 'website')
      },
      lockedSocialLabel () {
        const count = this.lockedSocialLinks.length

        return `Sign in to see ${count} more ${count === 1 ? 'channel' : 'channels'}`
      },
      /**
       * Section 06 is about *people* — named humans you can message.
       *
       * `hasContactDetails` used to keep it open too, from when the section also carried the
       * website/email/phone row. That row now lives with the identity block in section 01, so
       * including it here rendered a heading with nothing under it.
       */
      showContactsSection () {
        return this.hasContacts || (this.isLoggedIn && this.contributors.loading)
      },
      /*
       * The members-only sections are hidden outright for anonymous visitors rather than teased. No
       * gate, no placeholder — a visitor sees a shorter profile and is told nothing about what is
       * missing from it.
       *
       * One computed per section rather than the condition inline, because the section and its entry
       * in the page nav have to agree: a nav link to a section that was not rendered scrolls to
       * nothing.
       */

      /**
       * Members and the actor's own team. The whole of "03 / Offering" is behind the login now: the
       * offering prose already was (`hasOfferingDescription`), and leaving the taxonomy rows to
       * stand on their own gave a visitor a section titled "Products, capabilities and technology"
       * whose only content was a list of mapped terms — the section header promising the part that
       * was withheld. Hidden outright rather than shown as a teaser.
       *
       * The rows themselves stay public data — the ecosystem's `public_fields` serves technologies
       * and the ingredients/products domains to a guest, and they appear on this page in the domain
       * tags either way. This is about what the section is worth reading, not about the wire.
       *
       * The offering prose alone is enough to open the section: it is the actor's own answer to
       * "what you offer", and withholding it because no taxonomy row happens to be mapped hid the
       * one part of this section a member actually wrote.
       *
       * And for whoever can edit the profile it opens on nothing at all: the section carries the
       * only control that reaches the offering field, so hiding it on an empty profile made the
       * field unreachable on exactly the profiles that needed it.
       */
      showTechnologySection () {
        if (!this.isLoggedIn) {
          return false
        }

        return this.mappedCapabilityCount > 0 || this.hasOfferingDescription || this.showOwnerTools
      },
      showTransitionsSection () {
        return this.transitionEntries.length > 0
      },
      /**
       * Members only, per the UX matrix (Testimonials: visitor NA). Hidden entirely when there are
       * none — an empty "What others say" reads worse than no section at all, and writing one is
       * not something this page offers.
       */
      showTestimonialsSection () {
        return this.isLoggedIn && (this.testimonials.loading || this.testimonials.list.length > 0)
      },
      showOpportunitiesSection () {
        return this.isLoggedIn &&
          (this.seekingItems.length > 0 || this.offeringItems.length > 0 || this.matchCards.length > 0)
      },
      /**
       * Every numbered section, in document order, and whether it is rendered.
       *
       * One list so the page nav, the scroll-spy and the "01 /" labels cannot drift apart. Order
       * matters twice over: updateActiveSection() takes the last entry whose element is above the
       * nav, and the numbers below are positions in this list.
       *
       * Join and contacts are mutually exclusive — showContactsSection is false whenever
       * showJoinCta is true — so they can hold separate keys without both ever being counted.
       */
      /**
       * The sections worth a view event: the rail's, plus "more profiles", which is not a
       * destination in the rail but is the page's own exit into the rest of the ecosystem.
       */
      trackedSections () {
        return this.navSections.concat(
          this.nextActors.length ? [{ key: 'more', label: 'More profiles' }] : [],
        )
      },
      navSections () {
        return [
          { key: 'story', label: 'Profile', visible: true },
          { key: 'transitions', label: 'Transitions', visible: this.showTransitionsSection },
          { key: 'connections', label: 'Connections', visible: this.showConnectionsSection },
          { key: 'technology', label: 'Technology', visible: this.showTechnologySection },
          { key: 'opportunities', label: 'Opportunities', visible: this.showOpportunitiesSection },
          { key: 'join', label: 'Join', visible: this.showJoinCta },
          { key: 'contacts', label: 'Contact', visible: this.showContactsSection },
          { key: 'testimonials', label: 'Testimonials', visible: this.showTestimonialsSection },
        ].filter(section => section.visible)
      },
      /**
       * "01", "02", … keyed by section, counted over what is actually on the page.
       *
       * The numbers used to be written into the template by hand, so a visitor — who gets neither
       * the transitions nor the opportunities section — read "01 / The organisation" followed by
       * "04 / Technology", as though two sections had been withheld. They now close up.
       */
      sectionNumbers () {
        return this.navSections.reduce((byKey, section, index) => ({
          ...byKey,
          [section.key]: this.pad(index + 1),
        }), {})
      },
      /**
       * Every branch has to name a section that is actually rendered, or the closing CTA scrolls
       * nowhere. `hasContacts` was not enough on its own: a signed-in user with no claimed actor
       * gets the join pitch instead of the contacts section, however many contacts the actor has.
       *
       * The old "Find contact details" branch is gone — website, email and phone moved into the hero
       * identity block, so they are on screen before this button is, and there is nothing to
       * scroll to.
       */
      visitorAction () {
        if (this.showContactsSection) {
          return { label: this.$t('conversation_start'), section: 'contacts' }
        }

        if (this.showJoinCta) {
          return { label: 'Add my organisation', section: 'join' }
        }

        if (this.nextActors.length) {
          return { label: 'Browse similar actors', section: 'more' }
        }

        return null
      },
      /**
       * Whether there is a list to walk. The reader has to have arrived from one for "next" to
       * mean anything, which is the same thing `nextActors` needs in order to have entries — so
       * the control appears exactly when the "More profiles" section does.
       */
      canGoToNext () {
        return this.nextActors.length > 0
      },
      nextActors () {
        const actorsList = this.$store.state.actors.listData.data
        const index = actorsList.findIndex(actor => actor.id === this.actor.id)

        if (index === -1) {
          return []
        }

        if (index === actorsList.length - 1) {
          return actorsList.slice(0, AMOUNT_OF_ACTORS_IN_NEXT_ACTORS_SECTION)
        }

        return actorsList.slice(index + 1, index + 1 + AMOUNT_OF_ACTORS_IN_NEXT_ACTORS_SECTION)
      },
      nonProductClaims () {
        if (!this.profile || !this.profile.claims) {
          return []
        }

        return this.profile.claims.filter(claim => claim.actor_type !== 'Product')
      },
    },
    methods: {
      /** First letters of the first two words — the stand-in wherever a logo is missing. */
      initialsOf (name) {
        return (name || '')
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(part => part.charAt(0).toUpperCase())
          .join('')
      },
      /**
       * The line under a card in "more profiles".
       *
       * `short_description` is the server's own short line — the tagline when the actor has one,
       * the description trimmed to 240 characters otherwise — and it is translated alongside the
       * rest, so it is what the card gets rather than the long body. `description` is the fallback
       * for a payload that carries one without the other, which is why the trim is repeated here.
       */
      nextActorDescription (actor) {
        const description = this.localizedDisplayProperty(actor, 'short_description') ||
          this.localizedDisplayProperty(actor, 'description') ||
          ''

        return truncate(description, NEXT_ACTOR_DESCRIPTION_LIMIT)
      },
      /** One line about a connected actor, or nothing if we hold nothing worth showing. */
      connectionMeta (connection) {
        const category = connection.category

        return (category && (category.label || category.name)) || connection.country || null
      },
      /** Two-digit index for the numbered lists (products, transitions). */
      pad (value) {
        return String(value).padStart(2, '0')
      },
      /**
       * Guess which scale a report value is on and bring it to 0–100.
       *
       * The boundary is `< 1`, not `<= 1`: reports carry orchestration scores on a 1–5 scale, and
       * treating an exact 1 as the fractional scale made it render as 100 while 1.5 rendered as 30
       * — the least connected actors showed as the most connected. Six actors in the current
       * protein radar sit at exactly 1.
       *
       * Guessing at all is the underlying problem; scores read from actor_transition_scores are
       * already 0–100 and skip this path entirely.
       */
      normalizeScore (value) {
        const numeric = Number(value)

        if (!Number.isFinite(numeric) || numeric <= 0) {
          return null
        }

        if (numeric < 1) {
          return Math.round(numeric * 100)
        }

        if (numeric <= 5) {
          return Math.round(numeric * 20)
        }

        return Math.round(Math.min(100, numeric))
      },
      /**
       * Hierarchical taxonomies come back as a flat list containing both parents and
       * children. Prefer the leaves and use their parent as the category label.
       *
       * The parent key is not consistent across taxonomies: `technology` and
       * `product_features_*` use `parent_id`, while `domains` and `aspects` are
       * transformed to `parentId` (see OutgoingActorTransformer::transformDomains).
       */
      parentIdOf (item) {
        return item.parent_id || item.parentId || null
      },
      taxonomyRows (taxonomy) {
        const items = this.actor[taxonomy] || []
        const names = items.reduce((byId, item) => ({ ...byId, [item.id]: item.name }), {})
        const children = items.filter(item => !!this.parentIdOf(item))
        const rows = children.length ? children : items

        return rows
          .filter(item => !!item.name)
          .map(item => {
            const parentId = this.parentIdOf(item)

            return {
              id: item.id,
              name: item.name,
              category: parentId ? names[parentId] || null : null,
            }
          })
          // Alphabetical, and sorted before the cap so which six are shown is at least predictable
          // — the API order these arrive in is neither meaningful nor stable. localeCompare so
          // accented names sort where a reader expects rather than after z.
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
          .slice(0, 6)
      },
      metricsFromScore (score) {
        if (!score) {
          return []
        }

        return [
          { label: 'Readiness to scale', value: score.readiness_score },
          { label: 'Strategic impact', value: score.impact_score },
          { label: 'Connectedness', value: score.connectedness_score },
        ].filter(metric => Number.isFinite(metric.value))
      },
      /** Who said this and when — the design's "Actor input · reviewed Jul 2026" line. */
      provenanceLine (row) {
        const who = row.source === 'actor_input'
          ? 'Stated by the organisation'
          : (row.source === 'report' ? 'From a published report' : 'Suggested by classification')
        const when = row.reviewed_at ? `reviewed ${row.reviewed_at}` : null

        return [who, when].filter(Boolean).join(' · ')
      },
      evidenceLine (row) {
        if (!row.evidence || !row.evidence.length) {
          return null
        }

        return row.evidence.map(item => item.label).join(' · ')
      },
      /**
       * `source` names the control that asked for the jump, and is what makes the event worth
       * having — the same scroll is a top-bar CTA, a closing CTA and a match card's "start a
       * conversation", and untagged they would all land in one row. Left null by the section rail,
       * which reports its own navigation event instead.
       */
      scrollToSection (key, source = null) {
        const target = this.$refs[key]

        if (!target) {
          return
        }

        if (source) {
          trackFoodleapCta(TRACKING_PAGE, `Jump to ${key}`, source)
        }

        this.activeSection = key
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
      /**
       * Which sections a reader actually gets to, reported once each per profile view.
       *
       * `rootMargin` rather than a ratio threshold: these sections are taller than the viewport, so
       * a 20%-visible test would never fire for the long ones and would fire immediately for the
       * short ones. Shrinking the root to the middle half of the viewport asks the question we
       * actually want — is this section what the reader is looking at — at any section height.
       *
       * Re-runnable, and it has to be: the sections live behind `v-if="actor.id"` so there is
       * nothing to observe on first mount, and half of them appear later still — contacts and
       * testimonials when their own fetches land, "more profiles" when the actors list does. The
       * fired set is what keeps that safe, since rebuilding the observer re-reports whatever is on
       * screen otherwise.
       */
      observeSections () {
        if (!window.IntersectionObserver) {
          return
        }

        if (this._sectionObserver) {
          this._sectionObserver.disconnect()
        } else {
          this._sectionObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach(entry => {
                if (!entry.isIntersecting) {
                  return
                }

                const { sectionKey, sectionLabel } = entry.target.dataset

                this._firedSections.add(sectionKey)
                trackFoodleapSection(TRACKING_PAGE, sectionLabel)
                this._sectionObserver.unobserve(entry.target)
              })
            },
            { rootMargin: '-25% 0px -25% 0px' },
          )
        }

        this.observeContactsSection()

        this.trackedSections.forEach(section => {
          const element = this.$refs[section.key]

          if (!element || this._firedSections.has(section.key)) {
            return
          }

          element.dataset.sectionKey = section.key
          element.dataset.sectionLabel = section.label
          this._sectionObserver.observe(element)
        })
      },
      /** The section rail. Its own event, so wayfinding is not counted as intent to act. */
      onNavClick (section) {
        trackFoodleapClick(TRACKING_PAGE, 'Nav', section.label)
        this.scrollToSection(section.key)
      },
      /**
       * The transition tabs. Which of the four transitions readers open is the most specific thing
       * this page can tell us about what they came for, so it carries the transition's label.
       */
      selectTransition (index, entry) {
        this.activeTransition = index
        trackFoodleapClick(TRACKING_PAGE, 'Transition tab', entry.label)
      },
      toggleDescription () {
        this.showFullDescription = !this.showFullDescription
        trackFoodleapReadMore(TRACKING_PAGE, 'About', this.showFullDescription)
      },
      toggleOfferingDescription () {
        this.showFullOfferingDescription = !this.showFullOfferingDescription
        trackFoodleapReadMore(TRACKING_PAGE, 'Offering', this.showFullOfferingDescription)
      },
      /** For plain links with no handler of their own — the module function is not in scope there. */
      trackCta (label, section = null) {
        trackFoodleapCta(TRACKING_PAGE, label, section)
      },
      /** An outbound channel from the identity block — website, LinkedIn, email, phone. */
      trackSocialLink (link) {
        trackFoodleapClick(TRACKING_PAGE, 'Channel', link.name)
      },
      /**
       * The element this page actually scrolls in: the root itself on /actors/:id, the parent
       * page's pane when embedded. Everything that listens for scroll, reports depth or resets to
       * the top has to follow it up the tree — see isEmbeddedInParentPage.
       */
      resolveScrollContainer () {
        const root = this.$refs.scrollable

        if (!root) {
          return null
        }

        if (!this.isEmbeddedInParentPage) {
          return root
        }

        for (let node = root.parentElement; node; node = node.parentElement) {
          const { overflowY } = window.getComputedStyle(node)

          if (overflowY === 'auto' || overflowY === 'scroll') {
            return node
          }
        }

        return null
      },
      // Highlights the section currently under the sticky nav.
      updateActiveSection () {
        const container = this._scrollContainer

        if (!container) {
          return
        }

        const offset = container.getBoundingClientRect().top + 120
        const current = this.navSections
          .filter(section => {
            const element = this.$refs[section.key]

            return element && element.getBoundingClientRect().top <= offset
          })
          .pop()

        if (current) {
          this.activeSection = current.key
        }
      },
      shareProfile (source = null) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(window.location.href)
          this.showToast('Profile link copied')
        }

        trackHeapEvent('foodleapActorDetail.shareProfile')
        trackFoodleapCta(TRACKING_PAGE, 'Share profile', source)
      },
      showToast (message) {
        this.toast = message

        if (this.toastTimeout) {
          clearTimeout(this.toastTimeout)
        }

        this.toastTimeout = setTimeout(() => {
          this.toast = ''
        }, 2400)
      },
      /* ── profile upgrade wizard ──────────────────────────────────────── */

      /**
       * Hand the wizard everything it needs and show it.
       *
       * It lives in ModalsContainer, so context travels through the store and results come back over
       * $bus — see the listeners in mounted().
       */
      openEditor (step = 0, source = null) {
        trackFoodleapCta(TRACKING_PAGE, source || `Wizard step ${step}`, 'Owner tools')

        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          actor: this.actor,
          transitions: this.actorTransitions,
          adoptions: this.actorAdoptions,
          products: this.productItems,
          fallbackTransitions: this.wizardFallbackTransitions,
          step,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.PROFILE_UPGRADE)
      },
      applyUpgradedTransitions (transitions) {
        this.actorTransitions = transitions || []
      },
      applyUpgradedAdoptions (adoptions) {
        this.actorAdoptions = adoptions || []
      },
      /**
       * The join pitch. A signed-in user without a claimed actor is already registered, so they get
       * the add-actor modal (registered globally in ModalsContainer) rather than being sent through
       * registration again.
       */
      goToRegister (source = null) {
        trackHeapEvent('foodleapActorDetail.joinCta', { loggedIn: this.isLoggedIn })
        trackFoodleapCta(
          TRACKING_PAGE,
          this.isLoggedIn ? 'Add my organisation' : 'Register & add my organisation',
          source,
        )

        if (this.isLoggedIn) {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)

          return
        }

        this.$router.push('/register')
      },
      /**
       * Relationships are not part of the upgrade wizard — they are records between two actors, and
       * editing them belongs to the connections panel. Same call the simplified profile's
       * connections block makes; the panel reads the actor from the store, already loaded here.
       */
      openConnectionsPanel () {
        trackFoodleapCta(TRACKING_PAGE, 'Edit connections', 'Owner tools')

        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'actor-connections-panel',
        })
      },
      openEditSidePanel (actorId) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'actor-edit',
          metaData: { id: actorId },
        })
      },
      onClickClaimActor (source = null) {
        trackHeapEvent('foodleapActorDetail.showClaimModal')
        trackFoodleapCta(TRACKING_PAGE, 'Claim profile', source)
        this.$store.commit(ACTORS_MUTATION_TYPES.SET_ACTOR_FOR_PREVIEW, this.company)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CLAIM)
      },
      showSignIn (source = null) {
        trackFoodleapCta(TRACKING_PAGE, 'Sign in', source)

        if (this.$store.getters.isPrivate) {
          this.$router.push('/login')
        } else {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
        }
      },
      handleClickActorCard (actor, cardType = 'Actor card') {
        trackHeapEvent('foodleapActorDetail.clickSimilarActor')
        trackFoodleapActorVisit(cardType, actor.name)

        this.viewActor(actor)
      },
      /**
       * Stay on whichever route rendered this page: /actors/:id on Foodleap, where the augmented
       * profile is the default, and /actors/:id/aap where it is still reached as a preview.
       */
      viewActor (actor) {
        this.$router.push({
          name: this.$route.name,
          params: { ...this.$route.params, id: actor.id },
        })
      },
      /**
       * The next profile in the list the reader came from. Same walk as the classic and simplified
       * actor pages: step through `actors.listData`, and when we run off the end of the page ask
       * the store for the following one rather than looping back to the top.
       */
      goToNext () {
        const { data } = this.$store.state.actors.listData
        const next = 1 + data.findIndex(actor => actor.id === this.actor.id)

        trackHeapEvent('foodleapActorDetail.clickNext')
        trackFoodleapCta(TRACKING_PAGE, 'Next profile', 'Top bar')

        if (data[next]) {
          return this.viewActor(data[next])
        }

        if (this.$store.state.filters.paging.offset < this.$store.getters.lastPageOffset && data.length > 0) {
          this.$store.commit('FILTERS/NEXT_PAGE')
        }

        return this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_ACTORS_LIST_NOW, this.$store.getters.listFilterObject)
          .then(() => {
            // The new page can still open with the actor we are already on.
            const following = this.$store.state.actors.listData.data
              .find(actor => actor.id !== this.actor.id)

            if (following) {
              this.viewActor(following)
            }
          })
      },
      /**
       * The old actor detail page. It needs an address of its own because the augmented profile is
       * what /actors/:id renders on Foodleap now — ActorDetailVersionChooser hands over to the
       * classic page for this route.
       */
      goToClassicProfile () {
        this.$router.push(`/actors/${this.actor.id}/classic`)

        trackHeapEvent('foodleapActorDetail.goToClassicProfile')
        trackFoodleapCta(TRACKING_PAGE, 'Classic profile', 'Owner tools')
      },
      /**
       * Flip `?preview=visitor` on the current route.
       *
       * A query-only navigation: nothing watches anything but `$route.params.id`, so the page does
       * not refetch or remount and the reader keeps their scroll position. Pushed rather than
       * replaced so the browser's back button leaves the preview, and other query params are
       * carried over rather than dropped.
       */
      toggleVisitorPreview () {
        const query = { ...this.$route.query }

        if (this.forcedVisitorPreview) {
          delete query.preview
        } else {
          query.preview = 'visitor'
        }

        this.$router.push({ path: this.$route.path, query })

        trackHeapEvent('foodleapActorDetail.toggleVisitorPreview', {
          previewing: !this.forcedVisitorPreview,
        })
        trackFoodleapCta(
          TRACKING_PAGE,
          this.forcedVisitorPreview ? 'Exit visitor view' : 'View as visitor',
          'Owner tools',
        )
      },
      /** Initials, for a contact with no photo — same treatment as the actor avatar. */
      contactInitials (contact) {
        return this.initialsOf(contact.user_name)
      },
      /**
       * The tile's subtitle: what this person does. Their function, or their department when no
       * function is set — the two were stacked as separate lines before, which left tiles two
       * different heights depending on which of them was filled in.
       */
      contactRole (contact) {
        return this.getExperienceLevelName(contact) || this.getDepartmentName(contact)
      },
      /**
       * The tile's tags. Only the department, and only when the subtitle is not already using it:
       * the contributors endpoint carries no expertise (see UserRepository::transformUser), so
       * there is nothing else here to tag a person with that would be true.
       */
      contactTags (contact) {
        const department = this.getDepartmentName(contact)

        if (!department || department === this.contactRole(contact)) {
          return []
        }

        return [department]
      },
      /**
       * The line of bio under the name.
       *
       * Whitespace is collapsed: bios are stored as free text and are written with line breaks in
       * them, which `line-clamp-2` honours — a bio that opened with a short line then spent the
       * tile's second line on nothing. The drawer shows the text as written.
       */
      contactBio (contact) {
        return (contact.bio || '').replace(/\s+/g, ' ').trim()
      },
      /**
       * Open the existing conversation, or create one and go to it.
       *
       * Mirrors ActorDetailContributors: the conversation list has to be refreshed before the
       * route change, or the new thread is missing from the sidebar it lands in.
       */
      async messageContact (contact, source = 'Contacts') {
        trackHeapEvent('foodleapActorDetail.messageContact')
        trackFoodleapCta(
          TRACKING_PAGE,
          contact.conversation_id ? 'Open chat' : 'Message contact',
          source,
        )

        if (contact.conversation_id) {
          this.$router.push(`/profile/conversations/${contact.conversation_id}`)

          return
        }

        try {
          const conversation = await Conversations.post({ recipient_id: contact.user_id })

          await this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          this.$router.push(`/profile/conversations/${conversation.id}`)
        } catch (errors) {
          this.showToast('Could not start the conversation')
          console.error(errors)
        }
      },
      /**
       * The hero's action card. It hands its own name down rather than reporting a CTA itself: each
       * action is reachable from its own section too, so the source is the only thing that differs
       * and tracking here as well would count one click twice.
       */
      runVisitorAction (action) {
        action.run('Hero actions')
      },
      /**
       * The hero's conversation action. A single contact is unambiguous, so it opens that chat
       * outright; with several, who to write to is the reader's call and the contacts section is
       * where they make it.
       */
      startConversationWithActor (source = 'Hero actions') {
        const contacts = this.messageableContacts

        if (contacts.length === 1) {
          this.messageContact(contacts[0], source)

          return
        }

        this.scrollToSection('contacts', source)
      },
      /**
       * The shared testimonial modal, which reads its subject from the modal context — the same
       * handoff SimplifiedActorDetailTestimonials uses, so both pages write through one form.
       */
      openTestimonialModal (source = 'Hero actions') {
        trackHeapEvent('foodleapActorDetail.writeTestimonial')
        trackFoodleapCta(TRACKING_PAGE, 'Write a testimonial', source)

        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { targetActor: this.actor })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CREATE_TESTIMONIAL)
      },
      /**
       * The person behind a contact card. `user-information-side-panel` reads only
       * `metaData.userId`, and it is the same panel the community member cards, the conversations
       * list and the settings user list open — so a contact here looks the same everywhere.
       */
      openUserPanel (contact) {
        trackHeapEvent('foodleapActorDetail.viewContact')
        trackFoodleapCta(TRACKING_PAGE, 'View contact', 'Contacts')

        this.showUserPanel(contact)
      },
      /** The commit on its own: the automatic open below is not a click and reports no CTA. */
      showUserPanel (contact) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'user-information-side-panel',
          metaData: { userId: contact.user_id },
        })
      },
      /**
       * The contacts section and its drawer are one thing: the tiles are a row of faces, and what
       * you came to this section for — the bio, the expertise, the way to start a conversation —
       * is in the drawer. So reaching the section opens the first person in it, and leaving the
       * section puts it away again.
       *
       * Deliberately narrow about when it acts:
       *   · never on a phone, where the drawer is full-width and would bury the section it belongs to
       *   · never over an already-open panel, whether a contact the reader picked or another
       *     panel entirely
       *   · never re-opened after the reader closes it by hand — the observer only fires on the
       *     way in, so a dismissal stands until they leave the section and come back
       */
      autoOpenFirstContact () {
        if (this.$store.getters.isMobile) {
          return
        }

        // Contributors arrive a moment after the actor, so the section can scroll into view while
        // the list is still loading. `fetchContributors` retries this once it has them.
        if (this.contributors.loading || this.$store.state.ui.sidePanel.isVisible) {
          return
        }

        const first = this.messageableContacts[0]

        if (first) {
          this.showUserPanel(first)
        }
      },
      /** Only ever the contact drawer: another panel open over this section is not ours to close. */
      closeContactDrawer () {
        if (!this.openContactUserId) {
          return
        }

        this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      },
      /**
       * Tracks the contacts section in and out of view, and unlike the analytics observer it keeps
       * watching rather than unobserving on the first hit — the drawer follows the section for as
       * long as the reader is on the page.
       */
      observeContactsSection () {
        if (this._contactsObserver) {
          this._contactsObserver.disconnect()
          this._contactsObserver = null
        }

        const element = this.$refs.contacts

        if (!element || !window.IntersectionObserver) {
          return
        }

        this._contactsObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              this._contactsInView = entry.isIntersecting

              if (entry.isIntersecting) {
                this.autoOpenFirstContact()
              } else {
                this.closeContactDrawer()
              }
            })
          },
          // The middle half of the viewport, as with the section-view observer: the question is
          // whether this section is what the reader is looking at, not whether a pixel of it shows.
          { rootMargin: '-25% 0px -25% 0px' },
        )

        this._contactsObserver.observe(element)
      },
      /** `section` says which of the two controls opened it — the top bar or the Contacts section. */
      openContributorModal (section) {
        trackFoodleapCta(TRACKING_PAGE, 'Manage contributors', section)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_CONTRIBUTOR_MODAL)
      },
      toggleExpertise () {
        this.expertiseExpanded = !this.expertiseExpanded

        if (this.expertiseExpanded) {
          trackFoodleapClick(TRACKING_PAGE, 'Expertise', 'Show all')
        }
      },
      /** Says where the icon goes, not just which goal it is — see the note on the row. */
      sustainabilityGoalTooltip (goal) {
        return `${goal.label} — see every organisation working on it`
      },
      /** Same jump the SDG icons make everywhere else: the directory, filtered to that goal. */
      filterBySustainabilityGoal (goal) {
        trackFoodleapClick(TRACKING_PAGE, 'SDG', goal.label)

        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$store.commit(FILTERS_MUTATION_TYPES.SET_BY_FACET, {
          facet: 'sustainability_goal',
          values: [goal.value],
        })
        this.$router.push('/actors-simplified')
      },
      /**
       * Received testimonials only, and only the visible ones — the endpoint also returns what this
       * actor has sent and what it has hidden, neither of which is anyone else's business.
       */
      fetchTestimonials () {
        if (!this.isLoggedIn) {
          this.testimonials = { list: [], loading: false }

          return
        }

        this.testimonials = { list: [], loading: true }

        getTestimonialsByActor(this.$route.params.id)
          .then(response => {
            const received = (response && response.received) || []

            this.testimonials = {
              list: received.filter(testimonial => !testimonial.isPending),
              loading: false,
            }
          })
          .catch(() => {
            this.testimonials = { list: [], loading: false }
          })
      },
      fetchContributors () {
        this.contributors = { list: [], loading: true }

        fetchContributors(this.$route.params.id, { includeOwners: true })
          .then(list => {
            const contacts = Object.keys(list)
              .map(key => list[key])
              .filter(contact => {
                // `allow_conversation` is the contactable flag the classic profile's contributor
                // list toggles, and it is honoured here for everyone - the actor's own team
                // included. SimplifiedActorDetail hands its team the unfiltered list because
                // ActorDetailContributors gives them the toggle to flip and the row to flip it on;
                // these cards deliberately carry none of those admin affordances (see the note on
                // the grid), so a contributor marked not contactable would appear here with a
                // Message button and nothing on the card to explain why they should not have one.
                if (!contact.allow_conversation) {
                  return false
                }

                // Your own card is kept for the actor's own team: it is the "This is you" tile and
                // the prompt to fill in what the card is still missing. For anyone else it would
                // only be an offer to message themselves.
                return this.isActorTeam || contact.user_id !== this.$store.getters.userId
              })

            this.contributors = {
              list: contacts,
              loading: false,
            }

            // The section was already on screen while this was loading, so the observer has
            // fired and will not fire again.
            if (this._contactsInView) {
              this.autoOpenFirstContact()
            }
          })
          .catch(() => {
            this.contributors = { list: [], loading: false }
          })
      },
      /**
       * Transition relevance comes from the ecosystem-level reports (there is no per-actor
       * endpoint), which are only reachable for authenticated users. Failures are silent:
       * the section simply stays hidden.
       */
      async fetchTransitionRelevance () {
        // Claims carry the per-transition score the blocks show, so they are fetched for visitors
        // too — the endpoint hands a guest the score and nothing else (see
        // ActorTransitionController::index). Everything below needs a login: the radar and pulse
        // reports sit behind `auth` middleware, and the adoptions feed the members-only technology
        // section, so asking for them as a guest only buys a 401.
        if (!this.isLoggedIn) {
          await this.fetchActorTransitionClaims()

          return
        }

        await Promise.all([
          this.fetchActorTransitionClaims(),
          this.fetchActorAdoptions(),
          this.fetchRadarPosition(),
          this.fetchPulseMentions(),
        ])
      },
      /**
       * Recorded claims. Failures are silent for the same reason the report calls are: the
       * section falls back to the pulse/radar view rather than breaking the page.
       */
      async fetchActorAdoptions () {
        try {
          const response = await fetchActorTechnologyAdoptions(this.$route.params.id)

          this.actorAdoptions = (response && response.adoptions) || []
        } catch (e) {
          this.actorAdoptions = []
        }
      },
      /**
       * Silent on failure, like the report calls: the hero stat falls back to the actor's own
       * subsidiaries rather than the page breaking over a second database being unreachable.
       */
      async fetchActorLocations () {
        try {
          const response = await fetchActorLocations(this.$route.params.id)

          this.locations = (response && response.locations) || []
        } catch (e) {
          this.locations = []
        }
      },
      async fetchActorTransitionClaims () {
        try {
          const response = await fetchActorTransitions(this.$route.params.id)

          this.actorTransitions = (response && response.transitions) || []
        } catch (e) {
          this.actorTransitions = []
        }
      },
      /**
       * The staff operations panel. Lives in a modal rather than on the page: it is tooling, not
       * profile content, and the profile is what everything else here is for.
       */
      openOperations () {
        trackFoodleapCta(TRACKING_PAGE, 'Operations', 'Owner tools')

        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { actor: this.actor })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.AAP_OPERATIONS)
      },
      async fetchRadarPosition () {
        try {
          const reports = await fetchInnovationRadarQuarters()

          if (!Array.isArray(reports) || !reports.length) {
            return
          }

          const latest = [...reports].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0]
          const report = await fetchInnovationRadar(latest.id)
          const match = (report.actors || []).find(actor => actor.id === this.actor.id)

          if (!match) {
            return
          }

          this.radarPosition = { ...match, reportTitle: report.title || latest.title }
        } catch (e) {
          // Not entitled to the report, or no report published yet.
        }
      },
      async fetchPulseMentions () {
        try {
          const reports = await fetchTransitionPulseMonths()

          if (!Array.isArray(reports) || !reports.length) {
            return
          }

          const latest = [...reports].sort((a, b) => (b.year - a.year) || (b.month - a.month))[0]
          const report = await fetchTransitionPulse(latest.id)
          const categories = (report.report_content && report.report_content.categories) || {}

          this.pulseMentions = Object.keys(categories).reduce((mentions, key) => {
            const category = categories[key]
            const mention = ((category && category.actors) || [])
              .find(actor => actor.elastic_id === this.actor.id)

            if (mention) {
              mentions.push({ key, title: category.title, meta: mention.meta })
            }

            return mentions
          }, [])
        } catch (e) {
          // Not entitled to the report, or no report published yet.
        }
      },
      fetch (force) {
        this.isInitialLoad = !force
        this.radarPosition = null
        this.pulseMentions = []
        this.actorTransitions = []
        this.actorAdoptions = []
        this.locations = []
        this.testimonials = { list: [], loading: false }
        this.activeTransition = null
        this.showFullDescription = false
        this.showFullOfferingDescription = false

        const fetchActorPromise = this.$store.dispatch(
          ACTORS_ACTION_TYPES.FETCH_ACTOR_DETAIL,
          force === true || this.$route.params.id,
        )

        if (!fetchActorPromise) {
          this.fetchContributors()
          this.fetchTestimonials()
          this.fetchTransitionRelevance()
          this.fetchActorLocations()

          return
        }

        fetchActorPromise
          .then(() => {
            if (!this.nextActors.length) {
              this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_ACTORS_LIST_NOW, this.$store.getters.listFilterObject)
            }
          })
          .finally(() => {
            if (this.isInitialLoad) {
              trackHeapEvent('foodleapActorDetail.load', {
                id: this.actor.id,
                name: this.actor.name,
              })
            }

            this.isInitialLoad = false
            this.fetchContributors()
            this.fetchTestimonials()
            this.fetchTransitionRelevance()
            this.fetchActorLocations()
          })
      },
    },
    created () {
      // Which sections have already reported a view, so re-observing as the late-loading ones
      // appear does not report the ones on screen again. Deliberately not reactive: nothing renders
      // from it, and writing to a reactive Set from the observer would invalidate every render that
      // reads trackedSections. Set before `fetch()`, which is what starts the actor arriving.
      this._firedSections = new Set()
      this._contactsInView = false

      // The detail actor may already be in the store (i.e. coming from a list page),
      // in which case the `company` watcher never fires.
      this.actor = this.company || {}

      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      this.fetch()
    },
    mounted () {
      this.$bus.on('actorUpdate', (id) => {
        if (id === this.actor.id) {
          this.fetch(true)
        }
      })

      this.$bus.on('updateContributors', this.fetchContributors)
      // The profile modal writes to the user, not the actor, so the contributor list has to be
      // refetched for the filled-in card to appear.
      this.$bus.on('updateUserProfile', this.fetchContributors)

      // The upgrade wizard is rendered by ModalsContainer, so it is not a child of this page and
      // cannot emit to it. It reports what it wrote over the bus instead.
      this.$bus.on('foodleapProfileUpgrade:transitions', this.applyUpgradedTransitions)
      this.$bus.on('foodleapProfileUpgrade:adoptions', this.applyUpgradedAdoptions)
      this.$bus.on('foodleapProfileUpgrade:toast', this.showToast)

      // The operations modal is rendered by ModalsContainer too, so it reports over the bus. A
      // score migration writes claims and scores, which is what the transitions sections render.
      this.$bus.on('aapOperations:migrated', this.fetchActorTransitionClaims)

      // Resolved once and kept, so removeEventListener in beforeUnmount is guaranteed to be
      // handed the same element addEventListener got.
      this._scrollContainer = this.resolveScrollContainer()

      if (this._scrollContainer) {
        this._scrollContainer.addEventListener('scroll', this.updateActiveSection, { passive: true })
      }

      // The page scrolls inside an element, not the window — hence the container argument.
      this._scrollTracker = createScrollDepthTracker('Foodleap Actor Profile', this._scrollContainer)
      this._scrollTracker.start()

      // Covers the actor already being in the store on mount (arriving from a list page), where the
      // watcher below never fires.
      this.$nextTick(this.observeSections)
    },
    beforeUnmount () {
      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      this.$bus.off('actorUpdate')
      this.$bus.off('updateContributors')
      // Scoped to the handler: `off(type)` alone clears every listener for the event, and this one
      // is emitted from several modals that other pages may come to listen for.
      this.$bus.off('updateUserProfile', this.fetchContributors)
      this.$bus.off('foodleapProfileUpgrade:transitions')
      this.$bus.off('foodleapProfileUpgrade:adoptions')
      this.$bus.off('foodleapProfileUpgrade:toast')
      this.$bus.off('aapOperations:migrated')

      if (this._scrollContainer) {
        this._scrollContainer.removeEventListener('scroll', this.updateActiveSection)
      }

      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
      }

      this._scrollTracker?.stop()
      this._sectionObserver?.disconnect()
      this._contactsObserver?.disconnect()
    },
    watch: {
      company (value) {
        this.actor = value || {}
      },
      /**
       * A different profile is a different set of sections — and on the first load, the first set
       * there is. Re-observing on the id rather than on the object: the actor is reassigned by
       * every refetch, and re-registering the observers on each of those would report sections the
       * reader is sitting on a second time.
       */
      'actor.id' (id) {
        if (!id) {
          return
        }

        this._firedSections = new Set()
        this._contactsInView = false
        this.$nextTick(this.observeSections)
      },
      /** Contacts, testimonials and "more profiles" all render once their own fetch lands. */
      trackedSections () {
        this.$nextTick(this.observeSections)
      },
      /**
       * A testimonial written from this page belongs on this page. The modal reports its success
       * to the store rather than back to whoever opened it, so the close is the only signal here
       * — and re-fetching on a cancelled write costs one request.
       */
      '$store.state.ui.visibleModals' (modals, previous) {
        if ((previous || []).includes(MODAL_IDS.CREATE_TESTIMONIAL) &&
          !(modals || []).includes(MODAL_IDS.CREATE_TESTIMONIAL)) {
          this.fetchTestimonials()
        }
      },
      '$route.params.id' (to, from) {
        if (to === from) {
          return
        }

        this.fetch()

        if (this._scrollContainer) {
          this._scrollContainer.scrollTop = 0
        }
      },
    },
  })
</script>

<style lang="scss" scoped>
  .foodleap-actor-detail {
    // Augmented-profile palette — mirrors the design source (actor-profile/app/globals.css).
    --fl-ink: #33413e;
    --fl-green: #5cc61e;
    // --fl-green is a fill colour: as *text* it is ~2.4:1 on white and ~2:1 on mint, so anywhere a
    // green word sits on a light ground it uses this darker one instead (~6:1 on mint).
    --fl-green-ink: #4a7a2b;
    --fl-mint: #eaf6e6;
    --fl-pale: #f4f8f3;
    --fl-cream: #fcfcfa;
    --fl-sand: #f7f2e8;
    --fl-blue: #70c1ff;
    --fl-coral: #ef374e;
    --fl-line: #dfe8df;
    --fl-muted: #6f7d78;

    background: var(--fl-cream);
    color: var(--fl-ink);
    font-family: 'Fira Sans', Arial, sans-serif;

    ::selection {
      background: var(--fl-green);
      color: #fff;
    }

    /**
     * Embedded in a spotting area or a community — see isEmbeddedInParentPage.
     *
     * The parent page owns the scroller and the persistent navigation: its hero's tab bar is
     * `position: sticky` at z-index 19 (`.basic-hero__slot` in SpottingAreaHero / CommunityHero).
     * So the two pieces of chrome here that assume a page of their own stand down — the top bar
     * stops sticking and stops outranking that tab bar, and the section rail, which is
     * `position: fixed` to the viewport rather than to this column, goes away instead of floating
     * over the app's own navigation.
     */
    &.has-parent-page {
      .fl-topbar {
        position: static;
        z-index: auto;
      }

      .fl-sidenav {
        display: none;
      }
    }
  }

  /* --- shared type ----------------------------------------------------- */

  .fl-overline,
  .fl-section-no {
    display: block;
    text-transform: uppercase;
    letter-spacing: .12em;
    font-size: 10px;
    font-weight: 500;
    color: var(--fl-muted);
  }

  .fl-btn {
    border: 0;
    border-radius: 4px;
    padding: 15px 21px;
    font-family: inherit;
    font-weight: 500;
    font-size: 13px;
    cursor: pointer;

    span {
      margin-left: 12px;
    }

    transition: background-color .2s, color .2s;

    /* The two fills are inversions of each other, so the tiers stay distinguishable when both
       appear in one block. Ink on green rather than white: white on #5cc61e is ~2.2:1 and fails
       contrast, ink is ~5.5:1 and passes. */
    &--primary {
      background: var(--fl-green);
      color: var(--fl-ink);

      &:hover {
        background: var(--fl-ink);
        color: #fff;
      }
    }

    &--dark {
      background: var(--fl-ink);
      color: #fff;

      &:hover {
        background: var(--fl-green);
        color: var(--fl-ink);
      }
    }

    &--ghost {
      background: transparent;
      color: var(--fl-ink);
      border: 1px solid var(--fl-line);

      &:hover {
        border-color: var(--fl-ink);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &--small {
      padding: 11px 16px;
    }

    &:disabled {
      opacity: .5;
      cursor: default;
    }
  }

  .fl-textlink,
  .fl-textbtn {
    border: 0;
    background: none;
    padding: 0;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: inherit;
    cursor: pointer;

    &:hover,
    &.is-active {
      color: var(--fl-green);
    }

    // Previewing is a state you can forget you are in, so it stays marked while it is on.
    &.is-active {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  /* --- top bar --------------------------------------------------------- */

  .fl-topbar {
    min-height: 76px;
    padding: 12px 5vw;
    display: flex;
    align-items: center;
    // Everything sits at the trailing edge; the leading edge is the side rail's business now.
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 12px 20px;
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 30;
    border-bottom: 1px solid var(--fl-line);
  }

  .fl-topbar__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .fl-topbar__utils {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  /* --- side nav -------------------------------------------------------- */

  /**
   * The section rail. Fixed to the left gutter rather than in the flow, so it costs the sections no
   * width — they keep their 7vw padding and their full measure.
   *
   * Numbers are always shown; the label slides out on hover, focus and for the active section, so
   * the resting width is ~34px and fits a gutter that is only ~90px on a laptop.
   */
  .fl-sidenav {
    position: fixed;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 25;
    display: flex;
    flex-direction: column;
    gap: 2px;

    button {
      position: relative;
      display: flex;
      align-items: center;
      border: 0;
      background: none;
      padding: 7px 8px;
      border-radius: 5px;
      font-family: inherit;
      color: var(--fl-muted);
      cursor: pointer;
      transition: color .2s, background-color .2s;

      > b {
        font-size: 10px;
        font-weight: 500;
        letter-spacing: .06em;
      }

      // Out of flow so revealing it cannot reflow the rail or nudge the numbers sideways.
      > span {
        position: absolute;
        left: 100%;
        white-space: nowrap;
        padding: 6px 11px;
        margin-left: 4px;
        border-radius: 5px;
        background: var(--fl-ink);
        color: #fff;
        font-size: 11px;
        font-weight: 500;
        opacity: 0;
        transform: translateX(-4px);
        pointer-events: none;
        transition: opacity .18s, transform .18s;
      }

      &:hover,
      &:focus-visible,
      &.is-active {
        color: var(--fl-green-ink);
        background: var(--fl-mint);
      }

      &:hover > span,
      &:focus-visible > span {
        opacity: 1;
        transform: translateX(0);
      }

      &.is-active > b {
        color: var(--fl-green-ink);
      }

      @media (prefers-reduced-motion: reduce) {
        transition: none;

        > span {
          transition: none;
        }
      }
    }
  }

  /* --- activation ------------------------------------------------------ */

  // Sized so the section below breaks the fold on a laptop: at 680px the profile itself started
  // off-screen, and the headline is an invitation to read the profile, not a substitute for it.
  // Still a hero — the mint band and the loops carry the page's opening — just not a full screen of one.
  .fl-activation {
    // No floor any more: with the identity block and the facts band in here, the content sets the
    // height everywhere, and a min-height only ever added mint below a short profile.
    padding: 54px 7vw 44px;
    display: grid;
    grid-template-columns: 1.35fr .65fr;
    // Row gap only for the facts band below; the column gap is the 7vw gutter.
    gap: 40px 7vw;
    // `start`, not `center`: the identity block reads from the top of the band, and centring it
    // against a shorter card left the logo floating mid-air.
    align-items: start;
    background: var(--fl-mint);
    position: relative;
    overflow: hidden;

    // The facts band is the hero's own bottom row.
    > .fl-facts {
      grid-column: 1 / -1;
    }
  }

  // Scaled with the band: at their old size the two rings met in the middle of a 440px hero and
  // read as a shape behind the text rather than as two marks in opposite corners.
  .fl-loop {
    position: absolute;
    border: 40px solid rgba(255, 255, 255, .36);
    border-radius: 50%;
    transform: rotate(-24deg);

    &--a {
      width: 400px;
      height: 190px;
      right: -140px;
      top: -70px;
    }

    &--b {
      width: 330px;
      height: 155px;
      left: -190px;
      bottom: -80px;
      border-color: rgba(126, 181, 223, .18);
    }
  }

  .fl-activation__copy {
    position: relative;

    > p {
      font-size: 15px;
      line-height: 1.65;
      // Narrower than the column so the paragraph stays two or three lines instead of one long
      // ribbon — the shorter hero has no room for a fourth.
      max-width: 620px;
      margin: 0;
    }
  }

  // The card sets the hero's height whenever it is taller than the copy, so its own padding counts
  // twice: once at the top, once at the bottom.
  .fl-why-card {
    position: relative;
    background: #fff;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 24px 60px rgba(57, 79, 70, .12);

    strong {
      font-size: 15px;
    }

    > small {
      display: block;
      margin-top: 14px;
      color: var(--fl-muted);
      font-size: 11px;
    }
  }

  .fl-why-card__reason {
    display: grid;
    grid-template-columns: 28px 1fr;
    gap: 4px 10px;
    padding: 13px 0;
    border-bottom: 1px solid var(--fl-line);

    > b {
      color: var(--fl-green);
      font-size: 11px;
    }

    p {
      grid-column: 2;
      margin: 0;
      color: var(--fl-muted);
      font-size: 12px;
      line-height: 1.5;
    }
  }

  .fl-why-card__stat {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid var(--fl-line);

    strong {
      font-size: 22px;
      letter-spacing: -.04em;
    }

    span {
      font-size: 12px;
      color: var(--fl-muted);
    }
  }

  /* The action rows keep the reason rows' grid — number column, title, note beneath — so the two
     cards read as one component seen from either side of the claim. */
  .fl-why-card__action {
    display: grid;
    grid-template-columns: 28px 1fr auto;
    gap: 4px 10px;
    align-items: baseline;
    width: 100%;
    padding: 13px 0;
    border: 0;
    border-bottom: 1px solid var(--fl-line);
    background: none;
    font-family: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;

    > b {
      color: var(--fl-green-ink);
      font-size: 11px;
    }

    strong {
      font-size: 15px;
      transition: color .2s;
    }

    i {
      font-style: normal;
      color: var(--fl-muted);
      transition: color .2s, transform .2s;
    }

    p {
      grid-column: 2 / -1;
      margin: 0;
      color: var(--fl-muted);
      font-size: 12px;
      line-height: 1.5;
    }

    &:hover,
    &:focus-visible {
      strong {
        color: var(--fl-green-ink);
      }

      i {
        color: var(--fl-green-ink);
        transform: translate(2px, -2px);
      }
    }

    &:focus-visible {
      outline: 2px solid var(--fl-green);
      outline-offset: 3px;
    }

    @media (prefers-reduced-motion: reduce) {
      strong,
      i {
        transition: none;
      }

      &:hover i,
      &:focus-visible i {
        transform: none;
      }
    }
  }

  /* The small print closes the card, so the last row does not need a rule under it as well. */
  .fl-why-card--cta .fl-why-card__action:last-of-type {
    border-bottom: 0;
  }

  .fl-health {
    margin-top: 16px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    font-size: 11px;
    font-weight: 500;

    i {
      grid-column: 1 / -1;
      height: 5px;
      background: #e5eae4;
    }

    em {
      display: block;
      height: 100%;
      background: var(--fl-green);
    }
  }

  /* --- generic section ------------------------------------------------- */

  .fl-section {
    padding: 105px 7vw;

    h2 {
      font-size: clamp(42px, 5vw, 68px);
      line-height: .95;
      letter-spacing: -.05em;
      margin: 20px 0 0;
    }
  }

  .fl-section-title {
    // Anchors the section's edit control — see .fl-section-edit.
    position: relative;
    display: grid;
    grid-template-columns: 1.2fr .6fr;
    gap: 8vw;
    align-items: end;
    margin-bottom: 55px;

    > p {
      line-height: 1.7;
      color: var(--fl-muted);
      font-size: 14px;
      margin: 0;
    }
  }

  /* --- 01 the organisation --------------------------------------------- */

  .fl-profile-intro {
    background: #fff;
  }

  // Lives in the hero now, so it carries the page's h1 and sits on mint rather than white.
  .fl-profile-heading {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 24px;
    align-items: center;
    margin: 18px 0 22px;

    h1 {
      // An actor name is arbitrary length, unlike a hand-set headline, so this tops out well below
      // the design's display size and is allowed to break mid-word rather than overflow the band.
      font-size: clamp(32px, 4vw, 52px);
      letter-spacing: -.045em;
      margin: 6px 0 2px;
      line-height: 1.02;
      overflow-wrap: anywhere;
    }

    p {
      font-size: 19px;
      line-height: 1.4;
      margin: 0;
    }
  }

  /**
   * The contact channels behind the login: their icons, blurred, with the invitation next to them.
   *
   * The icons carry no href (see the template), so the blur is presentation and there is nothing in
   * the markup to read either — a visitor sees that there is more, not what.
   */
  .fl-social-locked {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 12px 4px 6px;
    border: 1px dashed var(--fl-line);
    border-radius: 999px;
    background: #fff;
    font-family: inherit;
    color: var(--fl-muted);
    cursor: pointer;
    transition: border-color .2s, color .2s;

    &:hover {
      border-color: var(--fl-green);
      color: var(--fl-ink);
    }

    &:focus-visible {
      outline: 2px solid var(--fl-green);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  .fl-social-locked__icons {
    display: flex;
    align-items: center;
    filter: blur(3px);
    opacity: .6;
    user-select: none;

    // Overlapped rather than spread out: a blurred row of six full-size icons reads as a smudge the
    // width of the card, where a short stack reads as "there are more of these".
    > span {
      display: grid;
      place-items: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 1px solid var(--fl-line);
      background: #fff;
      color: var(--fl-ink);

      + span {
        margin-left: -12px;
      }

      // Beyond four the stack stops saying anything new, and the label already carries the count.
      &:nth-child(n + 5) {
        display: none;
      }
    }
  }

  .fl-social-locked__label {
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  .fl-logo {
    width: 96px;
    height: 96px;
    border-radius: 8px;
    // White, not pale: on the mint band a --fl-pale tile is almost invisible, and most logos are
    // drawn for a white ground anyway.
    background: #fff center / contain no-repeat;
    box-shadow: 0 10px 30px rgba(57, 79, 70, .1);
    display: grid;
    place-items: center;
    font-size: 31px;
    font-weight: 500;
    position: relative;

    button {
      position: absolute;
      right: -6px;
      bottom: -6px;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 0;
      background: var(--fl-green);
      color: #fff;
      cursor: pointer;
      // The glyph, not the box: a button's own padding and inherited 31px line-height push the
      // plus off the circle's centre, so drop both and let the grid place it.
      display: grid;
      place-items: center;
      padding: 0;
      font-size: 18px;
      line-height: 1;
    }
  }

  // Sits on the mint band now that the identity block moved into the hero, which is where the
  // brand green stopped being readable as text.
  .fl-verified {
    font-size: 11px;
    font-weight: 500;
    color: var(--fl-green-ink);
  }

  .fl-location {
    font-size: 11px;
    color: var(--fl-muted);
    margin-top: 10px;
  }

  /**
   * The per-section edit control: one place, on every section an owner can edit. Same underlined
   * 10px text the page's other edit affordances use (`.fl-card-head`) — it is a quiet way into the
   * wizard, not a call to action competing with the section's own heading.
   *
   * Absolute rather than another grid child — the headers hosting it have their own column ratios
   * (`.fl-section-title`) or padding (`.fl-expertise`), and taking it out of flow leaves all of them
   * exactly as they were for a visitor, who never sees the control. On phones it drops back into the
   * flow under the overline, where there is no room beside the header.
   */
  .fl-section-edit {
    position: absolute;
    top: 0;
    right: 0;
    border: 0;
    background: none;
    padding: 0;
    font-family: inherit;
    font-size: 10px;
    text-decoration: underline;
    color: inherit;
    white-space: nowrap;
    cursor: pointer;

    // Follows the copy instead of the header, where the panel it sits in has no header of its own.
    &--inline {
      position: static;
      display: inline-block;
      margin-top: 22px;
    }

    &:hover {
      color: var(--fl-green);
    }

    &:focus-visible {
      outline: 2px solid var(--fl-green);
      outline-offset: 2px;
    }
  }

  .fl-editlink {
    border: 0;
    background: none;
    text-decoration: underline;
    font-family: inherit;
    font-size: 11px;
    color: inherit;
    cursor: pointer;
    align-self: start;
  }

  .fl-facts {
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    margin: 0;
    border: 1px solid var(--fl-line);
    border-radius: 7px;
    // Opaque, so the decorative loops pass behind the band rather than through the values.
    background: #fff;
    overflow: hidden;

    > div {
      padding: 22px;
      border-left: 1px solid var(--fl-line);
      position: relative;

      &:first-child {
        border-left: 0;
      }

      /**
       * Inverted rather than tinted. A mint cell put --fl-green on pale green inside a mint band —
       * three greens deep, and the score itself was the least legible thing in the row. Ink gives
       * the highlight somewhere to be seen on a green page, and it is the same dark-on-light move
       * the buttons and the score-help panel already make.
       */
      &.is-primary {
        background: var(--fl-ink);
        color: #fff;

        span {
          color: rgba(255, 255, 255, .72);
        }

        // ~4.6:1 on ink, where the same green is ~2.4:1 on white — the accent only becomes
        // readable once the ground is dark.
        em {
          color: var(--fl-green);
          font-size: 14px;
        }
      }
    }

    span {
      display: block;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .1em;
      color: var(--fl-muted);
      margin-bottom: 9px;
    }

    strong {
      font-size: 15px;
    }

    em {
      position: absolute;
      right: 20px;
      bottom: 19px;
      color: var(--fl-green);
      font-style: normal;
      font-weight: 500;
      font-size: 13px;
    }
  }

  .fl-company-grid {
    display: grid;
    // The story is the section, and the products card is a sidebar to it — the old 1.35 / .65 split
    // with an 8vw gutter left the copy in a column narrower than the empty space beside it.
    grid-template-columns: minmax(0, 1.9fr) minmax(240px, .6fr);
    gap: 5vw;
  }

  .fl-company-story {
    position: relative;

    h3 {
      font-size: clamp(34px, 4vw, 55px);
      line-height: 1;
      letter-spacing: -.04em;
      margin: 25px 0;
    }
  }

  .fl-copy {
    color: var(--fl-muted);
    font-size: 19px;
    line-height: 1.7;
    // Long-form reading measure: the wider column above would otherwise run the story out to well
    // past 100 characters a line.
  }

  .fl-readmore button {
    border: 0;
    background: none;
    padding: 0;
    margin-top: 14px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 3px;
    color: inherit;
    cursor: pointer;

    &:hover {
      color: var(--fl-green);
    }
  }

  // The long-form offering, set as a second movement of the story rather than a new section: same
  // measure and colour as the description above it, just reintroduced by a rule and a label.
  .fl-offering {
    margin-top: 38px;
    padding-top: 30px;
    border-top: 1px solid var(--fl-line);

    h4 {
      font-size: 13px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: .1em;
      color: var(--fl-muted);
      margin: 0 0 14px;
    }

    // More air than the 14px the intro column gives the same control, above and below. This block
    // runs the full width of the section and the capability cards start straight underneath, so at
    // the intro's spacing the button reads as a label on what follows rather than as the end of the
    // text it opens.
    .fl-readmore {
      margin-top: 8px;
      margin-bottom: 34px;
    }

    .fl-offering__empty {
      margin: 0;
      font-size: 13px;
      color: var(--fl-muted);
    }
  }

  .fl-provenance {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 30px 0 0;
    font-size: 11px;
    color: var(--fl-muted);

    a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 3px;

      &:hover {
        color: var(--fl-green-ink);
      }
    }
  }

  .fl-glance {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    border-top: 1px solid var(--fl-line);
    margin-top: 38px;
    padding-top: 24px;

    b {
      font-size: 26px;
      display: block;
    }

    span {
      display: block;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .1em;
      color: var(--fl-muted);
      margin-top: 6px;
    }
  }

  .fl-products-card {
    background: var(--fl-pale);
    padding: 28px;
    border-radius: 7px;
    align-self: start;

    > a {
      display: grid;
      grid-template-columns: 26px 1fr auto;
      gap: 10px;
      padding: 18px 0;
      border-top: 1px solid #cbd8cb;
      font-size: 12px;
      cursor: pointer;

      > b {
        font-size: 9px;
        color: var(--fl-muted);
      }

      > i {
        font-style: normal;
      }

      &:hover {
        color: var(--fl-green);
      }
    }

    > small {
      display: block;
      margin-top: 14px;
      color: var(--fl-muted);
      font-size: 9px;
    }
  }

  .fl-card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: .1em;
    font-size: 10px;
    margin-bottom: 16px;

    button {
      border: 0;
      background: none;
      font-family: inherit;
      font-size: 10px;
      text-decoration: underline;
      color: inherit;
      cursor: pointer;
    }

    em {
      font-style: normal;
      color: var(--fl-muted);
    }
  }

  .fl-expertise {
    margin-top: 70px;
    padding: 40px;
    background: var(--fl-sand);
    display: grid;
    grid-template-columns: 1fr 1.1fr;
    gap: 7vw;
    align-items: center;

    h3 {
      font-size: 35px;
      line-height: 1;
      letter-spacing: -.04em;
      margin: 25px 0;
    }

    p {
      line-height: 1.6;
      color: var(--fl-muted);
      font-size: 14px;
      margin: 0;
    }
  }

  /**
   * One row per main domain, with its subdomains beside it. Same chips the transition section uses
   * for domains, so a subdomain reads as a tag on the domain rather than as a second domain.
   */
  .fl-expertise__domains {
    display: grid;
    gap: 8px;

    > div {
      background: #fff;
      padding: 15px 18px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      gap: 10px 16px;
      font-size: 14px;
    }

    b {
      font-weight: 500;
    }

    /* The panel's disclosure control. It replaced a bare count that was styled exactly like this
       and did nothing when it was clicked, so it keeps the look and gains the behaviour. */
    > .is-more {
      justify-self: start;
      border: 0;
      background: none;
      padding: 0;
      font: inherit;
      font-size: 11px;
      color: var(--fl-green);
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }

      &:focus-visible {
        outline: 2px solid var(--fl-green);
        outline-offset: 2px;
      }
    }
  }

  .fl-expertise__subs {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;

    > span {
      font-size: 11px;
      color: var(--fl-muted);
      background: #eef2ec;
      padding: 6px 9px;
      border-radius: 3px;
    }

    // A count, not a subdomain — so it is not dressed up as one. Muted rather than in the accent:
    // it is not a control, and the panel's one control sits below in the accent colour.
    > .is-count {
      background: none;
      padding: 6px 0;
      color: var(--fl-muted);
    }
  }

  /* A full-width strip along the bottom of the expertise panel. The panel is a two-column grid,
     so without the span this would drop into the left column beside the tags. */
  .fl-sdgs {
    grid-column: 1 / -1;

    /* Only when it follows the domain rows — as the panel's sole content it needs no divider. */
    &:not(:nth-child(2)) {
      margin-top: 8px;
      padding-top: 32px;
      border-top: 1px solid rgba(51, 65, 62, .12);
    }
  }

  .fl-sdgs__label {
    display: block;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: .12em;
    font-size: 10px;
    font-weight: 500;
    color: var(--fl-muted);
  }

  .fl-sdgs__row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-content: flex-start;

    button {
      display: block;
      width: 56px;
      height: 56px;
      padding: 0;
      border: 0;
      background: none;
      cursor: pointer;
      // The artwork is a full-bleed coloured square, so it carries its own edge; a border would
      // read as a second one.
      transition: transform .2s, opacity .2s;

      img {
        display: block;
        width: 100%;
        height: 100%;
      }

      &:hover {
        transform: translateY(-2px);
        opacity: .85;
      }

      &:focus-visible {
        outline: 2px solid var(--fl-green);
        outline-offset: 2px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      button {
        transition: none;

        &:hover {
          transform: none;
        }
      }
    }
  }

  /* --- 02 transitions -------------------------------------------------- */

  .fl-transitions {
    background: #fff;
  }

  .fl-transition-tabs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 7px;
    margin-bottom: 9px;

    button {
      border: 1px solid var(--fl-line);
      background: #fff;
      text-align: left;
      padding: 16px;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 5px;
      font-family: inherit;
      color: inherit;
      cursor: pointer;

      // Sized up a step: with the ring and the bars gone these tabs are the only place a score is
      // shown, so the number has to carry on its own rather than sit in a corner of a chip.
      b {
        font-size: 10px;
        color: var(--fl-muted);
        grid-column: 1 / -1;
      }

      span {
        align-self: center;
        font-weight: 500;
        font-size: 14px;
        line-height: 1.25;
      }

      em {
        align-self: center;
        font-style: normal;
        font-weight: 500;
        font-size: 22px;
        line-height: 1;
      }

      &[aria-selected='true'] {
        border-color: var(--fl-accent);
        box-shadow: inset 0 -5px var(--fl-accent);

        em {
          color: var(--fl-accent);
        }
      }

      // Disabled only in the sense that there is nothing to select, so it keeps the full-strength
      // colours a greyed-out control would lose.
      &:disabled {
        cursor: default;
        opacity: 1;
        color: inherit;
      }
    }

    // No panel below to point at, so nothing is "current" — every block reads the same.
    &.is-static button {
      box-shadow: inset 0 -5px var(--fl-accent);
      border-color: var(--fl-accent);

      em {
        color: var(--fl-accent);
      }
    }
  }

  /*
   * Visitors only. Same card language as the rest of the page — border, radius, generous padding,
   * an accent edge in the transition's own colour — because this text is the substance of the
   * section for a reader with no detail panel, not a footnote under a number.
   */
  .fl-transition-statements {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 7px;

    article {
      padding: 27px;
      border: 1px solid var(--fl-line);
      border-top: 4px solid var(--fl-accent);
      border-radius: 7px;
      background: #fff;

      p {
        margin: 12px 0 0;
        font-size: 15px;
        line-height: 1.55;
      }
    }
  }

  .fl-transition-detail {
    display: grid;
    // Two columns, not three: the ring and the bars are one reading of the same score, so they
    // share the first column and the narrative gets everything left. minmax keeps the ring from
    // being squeezed narrower than it draws.
    grid-template-columns: minmax(190px, .55fr) 1.6fr;
    gap: 4vw;
    padding: 45px;
    border: 1px solid var(--fl-line);
    border-radius: 7px;
  }

  // align-content: start so the pair sits at the top of a tall narrative rather than floating in
  // the middle of it.
  .fl-transition-scores {
    display: grid;
    align-content: start;
    gap: 40px;
  }

  .fl-score-block {
    text-align: center;

    > span {
      display: block;
      margin: 18px 0;
      font-size: 11px;
      font-weight: 500;
    }
  }

  .fl-score-ring {
    width: 158px;
    height: 158px;
    margin: auto;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: conic-gradient(var(--fl-accent) var(--fl-score), #e8ede7 0);
    position: relative;

    &:before {
      content: '';
      position: absolute;
      inset: 13px;
      background: #fff;
      border-radius: 50%;
    }

    strong {
      position: relative;
      z-index: 1;
      font-size: 48px;
      font-weight: 500;
      line-height: 1;
    }

    small {
      position: absolute;
      z-index: 1;
      bottom: 40px;
      font-size: 11px;
      color: var(--fl-muted);
    }
  }

  .fl-metrics {
    > div {
      margin-bottom: 22px;
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: baseline;
      gap: 8px;

      > i {
        grid-column: 1 / -1;
        height: 5px;
        background: #e5eae4;
      }

      > i > em {
        display: block;
        height: 100%;
        background: var(--fl-accent);
      }
    }

    span {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: .08em;
      color: var(--fl-muted);
    }

    b {
      font-size: 17px;
      font-weight: 500;
    }

    small {
      color: var(--fl-muted);
      font-size: 10px;
    }
  }


  // Dark on a white section, and open by default: this is the one block on the page that tells an
  // owner what to actually do, so it is styled to be found rather than to blend in. Still a
  // <details> so it can be collapsed once acted on.
  .fl-score-help {
    margin-top: 48px;
    // Real bottom padding rather than leaning on the last child's margin: with `padding-bottom: 0`
    // and no bottom border, that margin collapses out through the panel edge and the closing line
    // ends up flush against it. Only when open — closed, the summary's own padding is the whole box.
    padding: 0 28px;
    border-radius: 8px;
    background: var(--fl-ink);
    color: #fff;
    box-shadow: 0 24px 60px rgba(51, 65, 62, .22);

    > summary {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px 14px;
      padding: 22px 0;
      cursor: pointer;
      list-style: none;

      // The default triangle sits badly against this layout; the chevron below replaces it.
      &::-webkit-details-marker {
        display: none;
      }

      b {
        font-size: 17px;
        letter-spacing: -.02em;
      }

      span {
        flex: 1;
        font-size: 11px;
        color: rgba(255, 255, 255, .62);
      }

      // Chevron, rotated when open.
      &::after {
        content: '⌄';
        font-size: 15px;
        line-height: 1;
        color: var(--fl-green);
        transition: transform .18s ease;
      }
    }

    &[open] {
      padding-bottom: 28px;

      > summary {
        border-bottom: 1px solid rgba(255, 255, 255, .14);
        margin-bottom: 22px;

        &::after {
          transform: rotate(180deg);
        }
      }
    }

    p {
      font-size: 12.5px;
      line-height: 1.65;
      color: rgba(255, 255, 255, .82);
    }

    &__lead {
      margin: 0 0 22px;

      strong {
        color: var(--fl-green);
      }
    }

    &__list {
      list-style: none;
      margin: 0 0 22px;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 15px;

      li {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid rgba(255, 255, 255, .1);

        &:last-child {
          border-bottom: 0;
          padding-bottom: 0;
        }
      }

      b {
        display: block;
        margin-bottom: 5px;
        font-size: 12.5px;
        color: #fff;
      }

      span {
        font-size: 11.5px;
        line-height: 1.6;
        color: rgba(255, 255, 255, .66);
      }

      button {
        flex: none;
        border: 1px solid rgba(255, 255, 255, .28);
        border-radius: 4px;
        padding: 8px 13px;
        background: none;
        font-family: inherit;
        font-size: 10px;
        font-weight: 500;
        color: #fff;
        white-space: nowrap;
        cursor: pointer;
        transition: background .15s ease, border-color .15s ease, color .15s ease;

        &:hover {
          border-color: var(--fl-green);
          background: var(--fl-green);
          color: var(--fl-ink);
        }
      }
    }

    // The one thing an actor most needs to read, so it does not sit in the list as item six.
    &__highlight {
      margin-bottom: 20px;
      padding: 16px 18px;
      border-radius: 5px;
      border-left: 3px solid var(--fl-green);
      background: rgba(255, 255, 255, .07);

      b {
        display: block;
        margin-bottom: 6px;
        font-size: 12.5px;
        color: var(--fl-green);
      }

      p {
        margin: 0;
      }
    }

    // No bottom margin — the panel's own padding-bottom spaces this off the edge.
    &__caveat {
      margin: 0;
      font-size: 11px !important;
      color: rgba(255, 255, 255, .55) !important;
    }
  }

  .fl-transition-content {
    h3 {
      font-size: 31px;
      line-height: 1.12;
      letter-spacing: -.02em;
      margin: 14px 0 20px;
      font-weight: 500;
    }
  }

  .fl-domain-tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;

    span {
      font-size: 11px;
      background: #eef2ec;
      padding: 7px 10px;
      border-radius: 3px;
    }
  }

  .fl-narrative {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-top: 30px;

    > div {
      padding: 20px 22px;
      border-top: 1px solid var(--fl-line);
      border-left: 1px solid var(--fl-line);

      &:nth-child(odd) {
        border-left: 0;
      }

      // The odd cell out spans the row rather than leaving a hole beside it — with five narrative
      // fields there is always one.
      &:last-child:nth-child(odd) {
        grid-column: 1 / -1;
      }
    }

    span {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: .08em;
      color: var(--fl-muted);
    }

    // These are the actor's own account of its role, which is the point of the section — they were
    // set at 11px, small enough to read as a footnote.
    p {
      font-size: 15px;
      line-height: 1.65;
      margin: 10px 0 0;
    }
  }

  /* --- 03 connections -------------------------------------------------- */

  .fl-connections {
    background: var(--fl-pale);
  }

  .fl-connection-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
  }

  // Same card idiom as the products aside in section 01 — these are the same kind of thing (a short
  // list of other actors you can click through to), so they read as a set rather than as two
  // unrelated inventions.
  .fl-connection-group {
    background: #fff;
    border-radius: 7px;
    padding: 24px 26px;
    align-self: start;

    > a {
      display: grid;
      // logo · name+meta · arrow. The logo spans both text rows so it stays centred whether or not
      // a connection has a meta line.
      grid-template-columns: 34px 1fr auto;
      gap: 2px 12px;
      padding: 14px 0;
      border-top: 1px solid var(--fl-line);
      align-items: center;
      cursor: pointer;

      > b {
        grid-column: 2;
        font-size: 14px;
        font-weight: 500;
      }

      > i {
        grid-row: 1 / -1;
        grid-column: 3;
        align-self: center;
        font-style: normal;
        color: var(--fl-muted);
      }

      &:hover {
        > b {
          color: var(--fl-green-ink);
        }

        > i {
          color: var(--fl-green-ink);
        }
      }
    }

    .fl-connection-logo {
      grid-row: 1 / -1;
      grid-column: 1;
      width: 34px;
      height: 34px;
      border-radius: 5px;
      border: 1px solid var(--fl-line);
      // `contain`, not `cover`: a wordmark cropped to fill a square is unreadable.
      background: #fff center / contain no-repeat;
      display: grid;
      place-items: center;
      font-size: 11px;
      font-weight: 500;
      color: var(--fl-muted);
    }

    .fl-connection-meta {
      grid-column: 2;
      font-size: 11px;
      color: var(--fl-muted);
    }

    > small {
      display: block;
      padding-top: 14px;
      border-top: 1px solid var(--fl-line);
      font-size: 11px;
      color: var(--fl-muted);
    }
  }

  /* --- 04 technology --------------------------------------------------- */

  .fl-technology {
    background: var(--fl-mint);
  }

  .fl-actor-path {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    padding: 12px 18px;
    margin-bottom: 12px;
    font-size: 10px;
    flex-wrap: wrap;

    > span {
      background: var(--fl-green);
      color: #fff;
      padding: 7px 10px;
    }

    > i {
      font-style: normal;
      color: var(--fl-muted);
    }
  }

  .fl-tech-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .fl-tech-card {
    padding: 27px;
    background: #fff;
    border-radius: 6px;

    &--main {
      border-top: 6px solid var(--fl-green);
    }

    &--muted {
      opacity: .78;
      border-top: 6px solid var(--fl-blue);
    }
  }

  .fl-tech-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 15px;
    padding: 19px 0;
    border-top: 1px solid var(--fl-line);
    font-size: 11px;

    > b {
      font-size: 14px;
      font-weight: 500;
    }

    > span {
      color: var(--fl-muted);
    }

    > em {
      font-style: normal;
      text-transform: uppercase;
      color: var(--fl-green);
      font-size: 9px;
    }
  }

  /* --- 05 needs, offers & matches -------------------------------------- */

  .fl-opportunities {
    background: #fff;
  }

  .fl-needs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .fl-need-card {
    padding: 28px;
    border-radius: 6px;

    &--needs {
      background: #f7eadf;
    }

    &--offers {
      background: #edf3e4;
    }

    > span {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .1em;
    }

    h3 {
      margin: 20px 0;
      font-size: 18px;
      font-weight: 500;
    }

    > p {
      margin: 20px 0;
      font-size: 13px;
      color: var(--fl-muted);
    }
  }

  .fl-match-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    margin-top: 45px;

    article {
      border: 1px solid var(--fl-line);
      padding: 25px;
      min-height: 225px;
      display: flex;
      flex-direction: column;

      > span {
        color: var(--fl-green);
        font-size: 10px;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: .1em;
      }

      h3 {
        font-size: 22px;
        font-weight: 500;
        letter-spacing: -.02em;
        margin: 14px 0 8px;
      }

      p {
        color: var(--fl-muted);
        font-size: 11px;
        line-height: 1.5;
        margin: 0;
      }

      a {
        margin-top: auto;
        border-top: 1px solid var(--fl-line);
        padding-top: 14px;
        font-size: 11px;
        cursor: pointer;

        &:hover {
          color: var(--fl-green);
        }
      }
    }
  }

  /* --- contacts -------------------------------------------------------- */

  .fl-contacts {
    background: var(--fl-pale);
  }

  /* --- testimonials ---------------------------------------------------- */

  .fl-testimonials {
    background: #fff;
  }

  .fl-testimonial-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;

    > article {
      display: flex;
      flex-direction: column;
      background: var(--fl-pale);
      border-radius: 7px;
      padding: 26px 28px;
    }

    blockquote {
      margin: 0 0 18px;
      border: 0;
      padding: 0;
      font-size: 16px;
      line-height: 1.6;

      // Quotation marks in CSS rather than the string, so the content is never double-quoted when
      // an author wrote their own.
      &:before {
        content: '\201C';
      }

      &:after {
        content: '\201D';
      }
    }

    footer {
      margin-top: auto;
      padding-top: 16px;
      border-top: 1px solid var(--fl-line);

      b {
        display: block;
        font-size: 13px;
        font-weight: 500;
      }

      span,
      em {
        display: block;
        font-style: normal;
        font-size: 11px;
        color: var(--fl-muted);
      }

      em {
        margin-top: 4px;
      }
    }
  }

  .fl-testimonial-rating {
    margin-bottom: 12px;
    font-size: 13px;
    letter-spacing: 2px;
    color: var(--fl-line);

    .is-on {
      color: var(--fl-green);
    }
  }

  /* --- closing --------------------------------------------------------- */

  .fl-closing {
    text-align: center;
    padding: 110px 7vw;
    background: var(--fl-mint);

    h2 {
      font-size: clamp(38px, 5vw, 65px);
      line-height: 1;
      letter-spacing: -.04em;
      max-width: 920px;
      margin: 0 auto 28px;
    }

    // An actor name is arbitrary length, unlike the design's hand-set headline.
    h2.fl-closing__title--actor {
      font-size: clamp(32px, 4.2vw, 55px);
      overflow-wrap: anywhere;
    }

    p {
      color: var(--fl-muted);
      max-width: 560px;
      margin: 0 auto 30px;
      line-height: 1.6;
    }
  }

  .fl-closing__actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  /* --- more profiles --------------------------------------------------- */

  .fl-more {
    padding: 80px 7vw;
    background: #fff;

    .fl-section-no {
      margin-bottom: 24px;
    }
  }

  .fl-more__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;

    // SimplifiedImageCard ships unscoped styles sized for the classic pages; these bring the
    // description it now carries down to the page's own small muted text. `overflow: hidden` is
    // what makes the card's three-line clamp end in an ellipsis instead of a scroll area.
    //
    // Left-aligned, unlike the name above it: a centred paragraph of three ragged lines reads
    // badly. It takes `!important` because the card writes `text-align` as an inline style, and
    // its `leftAlignText` prop is no use here — that one would take the name left along with it.
    :deep(.simplified-connection__subtitle) {
      overflow: hidden;
      text-align: left !important;
      font-size: 12px;
      line-height: 1.6;
      color: var(--fl-muted);

      p {
        margin: 0;
      }
    }
  }

  /* --- responsive ------------------------------------------------------ */

  // The rail needs a gutter to sit in. Below this the sections are near full-bleed and it would
  // overlap their text, so it goes — the page is short enough to scroll at that width.
  @media (max-width: 1100px) {
    .fl-sidenav {
      display: none;
    }
  }

  @media (max-width: 900px) {

    .fl-activation,
    .fl-company-grid,
    .fl-expertise,
    .fl-section-title,
    .fl-transition-detail {
      grid-template-columns: 1fr;
    }

    .fl-transition-detail {
      gap: 35px;
    }

    // Stacked, the pair reads better side by side than as one tall column above the narrative.
    .fl-transition-scores {
      grid-template-columns: minmax(0, 200px) minmax(0, 1fr);
      align-items: center;
      gap: 30px;
    }


    .fl-tech-grid,
    .fl-needs-grid {
      grid-template-columns: 1fr;
    }

    .fl-profile-heading {
      grid-template-columns: auto 1fr;
    }

    .fl-editlink {
      grid-column: 2;
    }

  }

  @media (max-width: 560px) {
    .fl-topbar {
      padding: 12px 20px;
    }

    .fl-activation,
    .fl-section,
    .fl-closing,
    .fl-more {
      padding-left: 22px;
      padding-right: 22px;
    }

    // Too narrow for the ring and the bars beside each other, so back to one under the other.
    .fl-transition-scores {
      grid-template-columns: 1fr;
    }

    // Five narrative fields in two columns on a phone leaves nothing but hyphens.
    .fl-narrative {
      grid-template-columns: 1fr;

      > div {
        border-left: 0;
      }
    }

    .fl-activation {
      padding-top: 44px;
    }

    .fl-profile-heading {
      grid-template-columns: 1fr;
    }

    .fl-editlink {
      grid-column: 1;
      justify-self: start;
    }

    // Nothing fits beside a section header at this width, so it takes its own line under the
    // overline instead.
    .fl-section-edit {
      position: static;
      display: block;
      justify-self: start;
      margin: 10px 0 0;
    }

    .fl-facts {
      grid-template-columns: 1fr;

      > div {
        border-left: 0;
        border-top: 1px solid var(--fl-line);
      }
    }

    .fl-logo {
      width: 72px;
      height: 72px;
      font-size: 24px;
    }

    .fl-transition-tabs,
    .fl-transition-statements,
    .fl-narrative {
      grid-template-columns: 1fr;
    }

    .fl-narrative > div {
      border-left: 0;
    }

    .fl-tech-row {
      grid-template-columns: 1fr auto;

      > span {
        grid-column: 1 / -1;
      }
    }
  }

  @media print {
    .fl-topbar,
    .fl-sidenav,
    .fl-closing,
    .fl-more {
      display: none;
    }

    .fl-activation {
      min-height: auto;
    }

    .fl-section {
      padding: 50px 6vw;
    }

    .fl-match-row article {
      break-inside: avoid;
    }
  }
</style>
